import React, { useEffect, useState, useRef } from "react";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import { useChatAssistant } from "@/hooks/useChatAssistant";
import { useChatV2 } from "@/hooks/useChatV2";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/lib/supabase";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useToast } from "@/hooks/use-toast";
import { useAdminStatus } from "@/hooks/auth/useAdminStatus";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import { AIAssistantForm } from "@/components/super-admin/AIAssistantForm";
import { chatThreadsService } from "@/services/chatThreadsService";
import { titleGenerationService } from "@/services/titleGenerationService";
import { aiAssistantService } from "@/services/aiAssistantService";
import MainLayout from "@/components/layout/MainLayout";
import { Card, CardContent } from "@/components/ui/card";
import {
  MessageSquare,
  Plus,
  ArrowLeft,
  Bot,
  Settings,
  Edit,
  Copy,
  Archive,
  FileText,
  Image as ImageIcon,
  X,
  Eye,
  EyeOff,
  Mic,
  Wrench,
  Wand2,
  Menu,
} from "lucide-react";
import { ScrollArea } from "@/components/ui/scroll-area";
import { cn } from "@/lib/utils";

import { SaveChatMessageButton } from "@/components/ai-assistant/SaveChatMessageButton";
import { SaveToolOutputButton } from "@/components/ai-assistant/SaveToolOutputButton";
// Sheet already imported above; reuse the same import
import { Maximize2 } from "lucide-react";
import {
  Conversation,
  ConversationContent,
  ConversationScrollButton,
} from "@/components/ai-elements/conversation";
import { Message, MessageContent } from "@/components/ai-elements/message";
import {
  PromptInput,
  PromptInputTextarea,
  PromptInputSubmit,
  PromptInputToolbar,
  PromptInputTools,
  PromptInputButton,
  PromptInputModelSelect,
  PromptInputModelSelectTrigger,
  PromptInputModelSelectContent,
  PromptInputModelSelectItem,
  PromptInputModelSelectValue,
} from "@/components/ai-elements/prompt-input";
import { Loader } from "@/components/ai-elements/loader";
import { Response } from "@/components/ai-elements/response";
import { Actions, Action } from "@/components/ai-elements/actions";
import {
  Tool,
  ToolContent,
  ToolHeader,
  ToolInput,
  ToolOutput,
} from "@/components/ai-elements/tool";
import { AIContentRenderer } from "@/components/ai-assistant/AIContentRenderer";
import { SmartIcon } from "@/components/ui/SmartIcon";
import { FileUpload } from "@/components/ui/FileUpload";
import { ConnectorsMenu } from "@/components/agent/chat/ConnectorsMenu";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  InlineCitation,
  InlineCitationCard,
  InlineCitationCardBody,
  InlineCitationCardTrigger,
  InlineCitationCarousel,
  InlineCitationCarouselContent,
  InlineCitationCarouselItem,
  InlineCitationCarouselHeader,
  InlineCitationCarouselIndex,
  InlineCitationSource,
  InlineCitationText,
} from "@/components/ai-elements/inline-citation";
import {
  Reasoning,
  ReasoningContent,
  ReasoningTrigger,
} from "@/components/ai-elements/reasoning";
import {
  Sources,
  SourcesContent,
  SourcesTrigger,
  Source,
} from "@/components/ai-elements/source";

export default function ChatAssistantPage() {
  const { instanceId } = useParams();
  const location = useLocation() as any;
  const navigateRouter = useNavigate();
  const initialPromptFromState: string | undefined =
    location?.state?.initialPrompt;
  const initialThreadIdFromState: string | undefined =
    location?.state?.initialThreadId;
  const bootstrapKey = React.useMemo(
    () =>
      initialPromptFromState
        ? `${instanceId || "no-inst"}:${initialThreadIdFromState || "create"}:${initialPromptFromState}`
        : null,
    [instanceId, initialThreadIdFromState, initialPromptFromState],
  );
  const { user, organization, userProfile } = useAuth();
  const [previewImageUrl, setPreviewImageUrl] = useState<string | null>(null);
  const [previewImageName, setPreviewImageName] = useState<string>("");
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { isAdmin } = useAdminStatus();
  const [isEditOpen, setIsEditOpen] = useState(false);
  const handleOpenEditAssistant = () => {
    console.log("[ChatAssistant] Opening assistant form sheet");
    setShowAssistantEditModal(true);
  };

  const [threads, setThreads] = useState<
    Array<{ id: string; title: string | null; updated_at: string }>
  >([]);
  const [selectedThreadId, setSelectedThreadId] = useState<string | null>(null);
  const [initialMessages, setInitialMessages] = useState<
    Array<{ role: any; content: string }>
  >([]);
  const [assistantTitle, setAssistantTitle] = useState<string>("Chat");
  const [instanceData, setInstanceData] = useState<any>(null);
  const [instanceError, setInstanceError] = useState<string | null>(null);
  const [isLoadingInstance, setIsLoadingInstance] = useState<boolean>(true);

  // Settings modal state
  const [isSettingsModalOpen, setIsSettingsModalOpen] = useState(false);
  const [settingsForm, setSettingsForm] = useState({
    title: "",
    name: "",
    description: "",
    chat_welcome_message: "",
    custom_instructions: "",
    cta_label: "",
  });

  // Assistant edit modal state
  const [showAssistantEditModal, setShowAssistantEditModal] = useState(false);

  // Thread title editing state
  const [isEditingThreadTitle, setIsEditingThreadTitle] = useState(false);
  const [editingThreadTitle, setEditingThreadTitle] = useState("");
  const [isArchiving, setIsArchiving] = useState(false);

  // Mobile sidebar state
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);
  const [threadToArchive, setThreadToArchive] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<"my" | "team">("my");
  const [currentThread, setCurrentThread] = useState<any>(null);
  const [selectedTools, setSelectedTools] = useState<string[]>([]);
  const [savedMessageIds, setSavedMessageIds] = useState<Set<string>>(
    new Set(),
  );

  // Model selection state
  const [selectedModel, setSelectedModel] = useState<string>("");

  // Tools modal state (placeholder for future implementation)
  const [isToolModalOpen, setIsToolModalOpen] = useState(false);

  // Fetch base assistant data once we have instance data
  const {
    data: baseAssistant,
    isLoading: baseLoading,
    error: baseError,
  } = useQuery({
    queryKey: ["aiAssistant", instanceData?.ai_assistant_id],
    queryFn: async () => {
      const result = await aiAssistantService.getSystemAssistant(
        instanceData!.ai_assistant_id,
      );
      if (result.error) {
        throw result.error;
      }
      return result.data;
    },
    enabled: !!instanceData?.ai_assistant_id,
  });

  // Fetch linked tools for this chat assistant
  const { data: linkedTools, isLoading: linkedToolsLoading } = useQuery({
    queryKey: ["linkedTools", instanceData?.ai_assistant_id],
    queryFn: async () => {
      const result = await aiAssistantService.getLinkedToolsForChatAssistant(
        instanceData!.ai_assistant_id,
      );
      if (result.error) {
        console.warn("Failed to fetch linked tools:", result.error);
        return [];
      }
      return result.data;
    },
    enabled: !!instanceData?.ai_assistant_id,
  });

  // Safety check for required data
  if (!instanceId) {
    return (
      <MainLayout>
        <div className="p-6 text-center">
          <h1 className="text-xl text-red-600">
            Error: No instance ID provided
          </h1>
          <p className="text-gray-500">Please navigate back and try again.</p>
        </div>
      </MainLayout>
    );
  }

  // Show error if instance failed to load
  if (instanceError) {
    return (
      <MainLayout>
        <div className="p-6 text-center">
          <h1 className="text-xl text-red-600">{instanceError}</h1>
          <p className="text-gray-500">instanceId: {instanceId}</p>
          <p className="text-gray-500">Please navigate back and try again.</p>
        </div>
      </MainLayout>
    );
  }

  useEffect(() => {
    if (!instanceId) return;

    const loadInstanceData = async () => {
      try {
        setIsLoadingInstance(true);
        console.log("[ChatAssistant] Loading instance data for:", instanceId);

        const { data, error } =
          await aiAssistantService.getAssistantInstance(instanceId);
        console.log("[ChatAssistant] Instance data response:", { data, error });

        if (error) {
          console.error("Failed to load assistant instance:", error);
          setInstanceError("Failed to load assistant data");
          setIsLoadingInstance(false);
          return;
        }

        if (data) {
          console.log("[ChatAssistant] Setting instance data:", data);
          setInstanceData(data);
          setAssistantTitle(data.name || data.title || "Chat");
          // Handle existing models that might not have provider prefix
          let modelToSet = data.model || "default";
          if (modelToSet !== "default" && !modelToSet.includes("/")) {
            // Add provider prefix for old models
            switch (modelToSet) {
              case "gpt-4.1":
              case "gpt-5":
                modelToSet = `openai/${modelToSet}`;
                break;
              case "claude-sonnet-4-20250514":
                modelToSet = `anthropic/${modelToSet}`;
                break;
              case "grok-4":
                modelToSet = `xai/${modelToSet}`;
                break;
              case "gemini-2.5-flash":
              case "gemini-2.5-flash-image-preview":
                modelToSet = `google/${modelToSet}`;
                break;
            }
          }

          // Update the database with the corrected model format if needed
          if (modelToSet !== data.model && modelToSet !== "default") {
            console.log(
              "[ChatAssistant] Updating model format from",
              data.model,
              "to",
              modelToSet,
            );
            aiAssistantService
              .updateAssistantInstanceModel(instanceId, modelToSet)
              .catch(console.error);
          }
          setSelectedModel(modelToSet);
          setInstanceError(null);
        } else {
          setInstanceError("Assistant instance not found");
        }
      } catch (err) {
        console.error("Error loading assistant instance:", err);
        setInstanceError("Something went wrong with agent data");
      } finally {
        setIsLoadingInstance(false);
      }
    };

    loadInstanceData();
  }, [instanceId]);

  const reloadThreads = async () => {
    if (!organization?.id || !user?.id) return;
    const { data } = await chatThreadsService.listThreads(
      organization.id,
      instanceId || undefined,
      user.id,
      viewMode,
    );
    setThreads(
      (data || []).map((t: any) => ({
        id: t.id,
        title: t.title,
        updated_at: t.updated_at,
        is_public: t.is_public,
        user_id: t.user_id,
      })),
    );
  };

  useEffect(() => {
    reloadThreads();
  }, [organization?.id, user?.id, instanceId, viewMode]);

  // Auto-select latest thread when threads are loaded
  useEffect(() => {
    if (threads.length > 0 && !selectedThreadId) {
      const latestThread = threads[0]; // threads are ordered by updated_at desc
      console.log(
        "[ChatAssistant] Auto-selecting latest thread:",
        latestThread.id,
      );
      setSelectedThreadId(latestThread.id);
    }
  }, [threads, selectedThreadId]);

  // Load messages when thread changes (skip during bootstrap with initialPrompt to avoid race)
  useEffect(() => {
    if (!selectedThreadId) return;
    if (initialPromptFromState) return; // we'll rely on useChat+auto-send path for bootstrap
    console.log(
      "[ChatAssistant] Loading messages for thread:",
      selectedThreadId,
    );
    loadMessages(selectedThreadId);
  }, [selectedThreadId, initialPromptFromState]);

  const loadMessages = async (threadId: string | null) => {
    if (!threadId) {
      setInitialMessages([]);
      setMessages([]);
      setCurrentThread(null);
      return;
    }

    console.log("[ChatAssistant] Loading messages for thread:", threadId);

    // Load thread details
    const thread = threads.find((t) => t.id === threadId);
    setCurrentThread(thread || null);

    // Load selected tools for this thread
    if (thread?.selected_tools) {
      setSelectedTools(
        Array.isArray(thread.selected_tools) ? thread.selected_tools : [],
      );
    } else {
      setSelectedTools([]);
    }

    const { data: uiMessages, error } =
      await chatThreadsService.loadChatMessages(threadId);

    if (error) {
      console.error("[ChatAssistant] Error loading messages:", error);
      setInitialMessages([]);
      setMessages([]);
      return;
    }

    console.log("[ChatAssistant] Loaded messages:", uiMessages.length);
    setInitialMessages(uiMessages);
    setMessages(uiMessages);
  };

  // Use AI SDK 5.0 pattern with transport
  // Chat V2 endpoint only (no fallback)
  const CHAT_PATH = "/api/chat-v2";

  // For local dev, point directly to Express on 3001
  // Always use same origin for API calls (works for both dev and prod)
  const API_BASE = typeof window !== "undefined" ? window.location.origin : "";

  // Debug logging
  console.log("[ChatAssistant] Using chat-v2 (no fallback)");
  console.log("[ChatAssistant] API Base:", API_BASE);
  console.log("[ChatAssistant] Full Chat API URL:", `${API_BASE}${CHAT_PATH}`);
  // Get auth token synchronously
  const [authToken, setAuthToken] = useState<string | null>(null);

  useEffect(() => {
    const getToken = async () => {
      const { data } = await supabase.auth.getSession();
      const token = data.session?.access_token || null;
      console.log(
        "[ChatAssistant] Auth token retrieved:",
        token ? "Present" : "Missing",
      );
      console.log("[ChatAssistant] User:", user?.id);
      console.log("[ChatAssistant] Organization:", organization?.id);
      setAuthToken(token);
    };
    getToken();
  }, [user, organization]);

  // Store current files in a ref so fetch function can access them
  const currentFilesRef = useRef<any[]>([]);

  // Function to resolve the effective model to use
  const getEffectiveModel = () => {
    // When no override is chosen, always send "default" so the backend
    // uses the assistant's configured model reliably.
    if (!selectedModel || selectedModel === "default") {
      return "default";
    }
    return selectedModel;
  };

  const {
    messages,
    isLoading,
    streamingContent,
    toolActivities,
    setMessages,
    sendMessage,
    stop,
  } = useChatV2({
    apiUrl: `${API_BASE}${CHAT_PATH}`,
    threadId: selectedThreadId || undefined,
    assistantInstanceId: instanceId,
    organizationId: organization?.id,
    userId: user?.id,
    model: getEffectiveModel(),
    initialMessages,
    onFinish: (message, usage) => {
      console.log("[ChatAssistant] Message finished:", message);
      console.log("[ChatAssistant] Usage:", usage);
      // Reload threads after completion to update timestamps
      setTimeout(() => reloadThreads(), 1000);
    },
    onError: (error) => {
      console.error("[ChatAssistant] Stream error:", error);
      toast({
        title: "Error",
        description: error.message || "Failed to send message",
        variant: "destructive",
      });
    },
  });

  // Derive status for compatibility with existing UI code
  const status = isLoading ? "streaming" : "ready";

  // Create display messages including streaming content
  const displayMessages = React.useMemo(() => {
    if (isLoading) {
      // While streaming, add a temporary assistant message
      // Include tool activities in the message parts
      const streamingParts: any[] = [];

      // Add tool activities first (so they appear before text)
      toolActivities.forEach((activity) => {
        const state =
          activity.status === "pending"
            ? "input-available"
            : activity.status === "error"
              ? "output-error"
              : "output-available";

        streamingParts.push({
          type: `tool-${activity.toolName}`,
          toolCallId: activity.toolCallId,
          state: state,
          input: activity.args,
          result: activity.result,
          output: activity.result,
          errorText: activity.error,
        });
      });

      // Add streaming text content
      if (streamingContent) {
        streamingParts.push({ type: "text", text: streamingContent });
      } else if (streamingParts.length === 0) {
        // Only show empty text (loading dots) if no tool activities yet
        streamingParts.push({ type: "text", text: "" });
      }

      return [
        ...messages,
        {
          id: "streaming-temp",
          role: "assistant" as const,
          parts: streamingParts,
          isStreaming: true, // Flag to show typing indicator
          isWaitingForResponse:
            !streamingContent && toolActivities.length === 0, // New flag for loading state
        },
      ];
    }
    return messages;
  }, [messages, isLoading, streamingContent, toolActivities]);

  const [localInput, setLocalInput] = useState("");
  const [attachedFiles, setAttachedFiles] = useState<any[]>([]);

  // File upload refs for different modes
  const fileUploadRef = useRef<any>(null);
  const imageUploadRef = useRef<any>(null);

  // Manual dropdown state
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  // Tool management state
  const [toolInstances, setToolInstances] = useState<
    Array<{
      id: string;
      name: string;
      title: string;
      icon: string;
    }>
  >([]);
  // Read-only view now; no per-instance toggles

  useEffect(() => {
    const loadTools = async () => {
      console.log(
        "Loading AI tools for org:",
        organization?.id,
        "instance:",
        instanceId,
      );
      if (!organization?.id || !instanceId) return;
      try {
        // Resolve via base-level links
        const { data: chatInst } = await supabase
          .from("ai_assistant_instances")
          .select("ai_assistant_id")
          .eq("id", instanceId)
          .single();

        if (!chatInst?.ai_assistant_id) {
          setToolInstances([]);
          return;
        }

        const { data: baseLinks } = await (supabase as any)
          .from("assistant_generators")
          .select("generator_assistant_id")
          .eq("chat_assistant_id", chatInst.ai_assistant_id);

        const toolBaseIds = (baseLinks || []).map(
          (l: any) => l.generator_assistant_id,
        );
        if (toolBaseIds.length === 0) {
          setToolInstances([]);
          return;
        }

        // Try to find instances first (organization-specific)
        const { data: instances } = await supabase
          .from("ai_assistant_instances")
          .select(
            "id, name, ai_assistant_id, ai_assistants!inner(title, icon_url, supports_chat)",
          )
          .eq("organization_id", organization.id)
          .eq("is_active", true)
          .in("ai_assistants.id", toolBaseIds);

        let tools: Array<{
          id: string;
          name: string;
          title: string;
          icon: string;
        }> = [];

        if (instances && instances.length > 0) {
          // Use organization-specific instances if available
          tools = instances
            .filter(
              (r: any) =>
                r.ai_assistants && r.ai_assistants.supports_chat === false,
            )
            .map((r: any) => ({
              id: r.id as string,
              name: r.name || "AI Tool",
              title: r.ai_assistants?.title || "AI Tool",
              icon: r.ai_assistants?.icon_url || "🤖",
            }));
        } else {
          // Fallback: use base assistant data if no instances found
          console.log(
            "[ChatAssistant] No instances found, using base assistant data",
          );
          const { data: baseAssistants } = await supabase
            .from("ai_assistants")
            .select("id, title, icon_url, supports_chat")
            .in("id", toolBaseIds)
            .eq("supports_chat", false);

          tools = (baseAssistants || []).map((assistant: any) => ({
            id: assistant.id as string, // Use base assistant ID since no instance exists
            name: assistant.title || "AI Tool",
            title: assistant.title || "AI Tool",
            icon: assistant.icon_url || "🤖",
          }));
        }

        setToolInstances(tools);
      } catch (e) {
        console.error("Failed to load AI tool instances/links", e);
      }
    };
    loadTools();
  }, [organization?.id, instanceId]);

  // Removed toggle handler (read-only list)

  // Handle file uploads by triggering the FileUpload components
  const handleFileUpload = (mode: "file" | "image") => {
    if (mode === "image" && imageUploadRef.current) {
      const card = imageUploadRef.current.querySelector(".cursor-pointer");
      if (card) {
        card.click();
      }
    } else if (mode === "file" && fileUploadRef.current) {
      const card = fileUploadRef.current.querySelector(".cursor-pointer");
      if (card) {
        card.click();
      }
    }
  };

  // Remove a file from attached files
  const removeFile = (index: number) => {
    setAttachedFiles((prev) => prev.filter((_, i) => i !== index));
  };

  // Upload a file to Supabase Storage (similar to FileUpload component)
  const uploadFileToStorage = async (file: File) => {
    const fileId = `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    const fileName = `${fileId}-${file.name}`;
    const filePath = `assistant-files/${fileName}`;

    try {
      const { data, error } = await supabase.storage
        .from("agent_files")
        .upload(filePath, file, {
          cacheControl: "3600",
          upsert: false,
        });

      if (error) {
        console.error("Upload error:", error);
        throw error;
      }

      const {
        data: { publicUrl },
      } = supabase.storage.from("agent_files").getPublicUrl(filePath);

      // Return the uploaded file with the same structure as FileUpload component
      return {
        id: fileId,
        name: file.name,
        url: publicUrl,
        type: file.type,
        size: file.size,
      };
    } catch (error) {
      console.error("Failed to upload file:", error);
      toast({
        title: "Upload Failed",
        description: `Failed to upload ${file.name}`,
        variant: "destructive",
      });
      return null;
    }
  };

  const createNewThread = async () => {
    if (!organization?.id || !instanceId || !user?.id) return;

    console.log("[ChatAssistant] Creating new thread...");
    const { data: newThread, error } = await chatThreadsService.createThread(
      organization.id,
      instanceId,
      "New Chat",
      user.id,
    );

    if (error || !newThread) {
      console.error("[ChatAssistant] Error creating thread:", error);
      return;
    }

    console.log("[ChatAssistant] Created new thread:", newThread.id);

    // Add to threads list and select it
    const newThreadData = {
      id: newThread.id,
      title: "New Chat",
      updated_at: new Date().toISOString(),
    };

    setThreads((prev) => [newThreadData, ...prev]);
    setSelectedThreadId(newThread.id);

    // Clear messages for new thread
    setInitialMessages([]);
    setMessages([]);
  };

  // Bootstrap: select provided thread or create one (guarded)
  const lastBootstrapKeyRef = useRef<string | null>(null);
  useEffect(() => {
    const bootstrap = async () => {
      if (!initialPromptFromState) return;
      if (!organization?.id || !instanceId || !user?.id) return;
      if (selectedThreadId) return; // already in a thread
      if (bootstrapKey && lastBootstrapKeyRef.current === bootstrapKey) return;
      if (bootstrapKey) lastBootstrapKeyRef.current = bootstrapKey;
      if (initialThreadIdFromState) {
        // Trust pre-created thread id from index page
        setThreads((prev) => [
          {
            id: initialThreadIdFromState,
            title: "New Chat",
            updated_at: new Date().toISOString(),
          },
          ...prev,
        ]);
        setSelectedThreadId(initialThreadIdFromState);
      } else {
        // Fallback: create a thread here
        const { data: newThread } = await chatThreadsService.createThread(
          organization.id,
          instanceId,
          "New Chat",
          user.id,
        );
        if (!newThread?.id) return;
        setThreads((prev) => [
          {
            id: newThread.id,
            title: "New Chat",
            updated_at: new Date().toISOString(),
          },
          ...prev,
        ]);
        setSelectedThreadId(newThread.id);
      }
    };
    bootstrap();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    bootstrapKey,
    initialPromptFromState,
    initialThreadIdFromState,
    organization?.id,
    instanceId,
    user?.id,
  ]);

  // After thread is ready and chat status is ready, auto-send the initial prompt once
  const lastAutoSendKeyRef = useRef<string | null>(null);
  const pendingAutoPromptRef = useRef<string | null>(null);
  // Remember pending prompt when we bootstrap
  useEffect(() => {
    if (!initialPromptFromState) return;
    if (!selectedThreadId) return;
    if (bootstrapKey && lastAutoSendKeyRef.current === bootstrapKey) return;
    pendingAutoPromptRef.current = initialPromptFromState;
    setLocalInput(initialPromptFromState);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedThreadId, initialPromptFromState]);

  useEffect(() => {
    if (!pendingAutoPromptRef.current) return;
    if (!selectedThreadId) return;
    if (status !== "ready") return;
    if (bootstrapKey && lastAutoSendKeyRef.current === bootstrapKey) return;
    if (bootstrapKey) lastAutoSendKeyRef.current = bootstrapKey;

    const text = pendingAutoPromptRef.current;
    pendingAutoPromptRef.current = null;
    try {
      // Keep focus after sending to avoid UX friction
      sendMessage({
        content: text || "",
        parts: [{ type: "text", text }] as any,
      });
      // Clear the visible textarea so it doesn't retain the initial prompt
      setLocalInput("");
      const textarea = document.querySelector<HTMLTextAreaElement>(
        'textarea[name="message"]',
      );
      if (textarea) textarea.focus();
      navigateRouter(".", { replace: true, state: {} });
    } catch (e) {
      console.error("[ChatAssistant] Auto-send failed", e);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status, selectedThreadId]);

  // After stream completes, reconcile UI with DB to prevent temporary disappearance
  useEffect(() => {
    if (!selectedThreadId) return;
    if (status !== "ready") return;
    // Small debounce to wait for server persistence
    const t = setTimeout(async () => {
      try {
        const { data } =
          await chatThreadsService.loadChatMessages(selectedThreadId);
        if (Array.isArray(data)) {
          setInitialMessages(data);
          setMessages(data);
        }
      } catch (e) {
        // ignore
      }
    }, 250);
    return () => clearTimeout(t);
  }, [status, selectedThreadId]);

  const submitMessage = async () => {
    const text = localInput.trim();
    const hasFiles = attachedFiles.length > 0;

    if (
      (!text && !hasFiles) ||
      status === "submitted" ||
      status === "streaming"
    )
      return;

    console.debug("[Chat] submitMessage called with:", {
      text,
      filesCount: attachedFiles.length,
    });

    try {
      // Store files in ref so fetch function can access them (same format as AI tool)
      // IMPORTANT: Remove base64 data to avoid payload size issues - only keep URLs like AI tool
      const filesForBackend = hasFiles
        ? attachedFiles.map((file) => ({
            id: file.id,
            name: file.name,
            url: file.url,
            type: file.type,
            size: file.size,
            // Explicitly exclude base64 to keep payload small
          }))
        : [];

      currentFilesRef.current = filesForBackend;

      // Send message with proper AI SDK parts format
      const parts = [];

      // Add text part if there's text
      if (text) {
        parts.push({ type: "text", text });
      }

      // Add file parts if there are files
      if (hasFiles) {
        attachedFiles.forEach((file) => {
          parts.push({
            type: "file",
            mediaType: file.type,
            url: file.url,
            filename: file.name,
          });
        });
      }

      const messageParts =
        parts.length > 0
          ? parts
          : [
              {
                type: "text",
                text: text || "I have uploaded some files for you to analyze.",
              },
            ];

      await sendMessage({
        content: text || "I have uploaded some files for you to analyze.",
        parts: messageParts,
        inputData: hasFiles ? { Files: attachedFiles } : undefined,
      });

      console.debug("[Chat] sendMessage dispatched with files");
    } catch (err) {
      console.error("[Chat] sendMessage error", err);
    } finally {
      // Clear input and files after sending
      setLocalInput("");
      setAttachedFiles([]);
      // Clear files ref after request is sent
      setTimeout(() => {
        currentFilesRef.current = [];
      }, 1000);
    }
  };

  useEffect(() => {
    console.debug("[Chat] status:", status, "messages:", messages.length);
  }, [status, messages.length]);

  // Keep input focused after a response completes
  useEffect(() => {
    if (status === "ready") {
      const textarea = document.querySelector<HTMLTextAreaElement>(
        'textarea[name="message"]',
      );
      if (textarea) textarea.focus();
    }
  }, [status]);

  // Title generation after first assistant response
  useEffect(() => {
    const handleTitleGeneration = async () => {
      console.log("[ChatAssistant] Title generation check:", {
        messagesLength: messages.length,
        selectedThreadId,
        status,
        firstMessageRole: messages[0]?.role,
        secondMessageRole: messages[1]?.role,
        messages: messages.map((m) => ({
          role: m.role,
          content:
            typeof m.content === "string"
              ? m.content.substring(0, 50) + "..."
              : "[object]",
        })),
      });

      // Only generate title if we have exactly 2 messages (user + assistant)
      // and a thread ID and status is ready (conversation completed)
      if (
        messages.length === 2 &&
        selectedThreadId &&
        status === "ready" &&
        messages[0]?.role === "user" &&
        messages[1]?.role === "assistant"
      ) {
        const firstUserMessage = messages[0];
        const messageText =
          firstUserMessage.content ||
          (Array.isArray(firstUserMessage.parts)
            ? firstUserMessage.parts.find((p) => p.type === "text")?.text
            : firstUserMessage.text) ||
          "";

        if (messageText && messageText.trim().length > 0) {
          console.log(
            "[ChatAssistant] Generating title for first message:",
            messageText.substring(0, 50) + "...",
          );

          try {
            const { data: generatedTitle, error } =
              await titleGenerationService.generateTitle(messageText);

            if (generatedTitle && !error) {
              console.log("[ChatAssistant] Generated title:", generatedTitle);

              // Update the thread title in the database
              const { data, error: updateError } =
                await chatThreadsService.updateThreadTitle(
                  selectedThreadId,
                  generatedTitle,
                );

              if (updateError) {
                console.error(
                  "[ChatAssistant] Failed to update thread title:",
                  updateError,
                );
              } else {
                console.log(
                  "[ChatAssistant] Successfully updated thread title",
                );
                // Optionally refresh the threads list to show the new title
                queryClient.invalidateQueries({ queryKey: ["chat-threads"] });
              }
            } else {
              console.warn("[ChatAssistant] Title generation failed:", error);
            }
          } catch (error) {
            console.error("[ChatAssistant] Error in title generation:", error);
          }
        }
      }
    };

    handleTitleGeneration();
  }, [messages, selectedThreadId, status, queryClient]);

  // Initialize settings form when modal opens or instanceData changes
  useEffect(() => {
    if (isSettingsModalOpen && instanceData && baseAssistant) {
      setSettingsForm({
        title: instanceData.title || baseAssistant.title || "",
        name: instanceData.name || baseAssistant.name || "",
        description:
          instanceData.description || baseAssistant.description || "",
        chat_welcome_message:
          (baseAssistant as any)?.chat_welcome_message || "",
        custom_instructions: instanceData.custom_instructions || "",
        cta_label: instanceData.cta_label || baseAssistant.cta_label || "Send",
      });
    }
  }, [isSettingsModalOpen, instanceData, baseAssistant]);

  // Mutation for updating assistant instance
  const updateInstanceMutation = useMutation({
    mutationFn: async (updates: typeof settingsForm) => {
      if (!instanceId) throw new Error("No instance ID");

      const { data, error } = await aiAssistantService.updateAssistantInstance(
        instanceId,
        updates,
      );
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["aiAssistantInstance", instanceId],
      });
      setIsSettingsModalOpen(false);
      toast({
        title: "Settings saved",
        description: "Assistant settings have been updated.",
      });
      // Reload instance data to update the title
      if (instanceId) {
        const loadInstanceData = async () => {
          const { data } =
            await aiAssistantService.getAssistantInstance(instanceId);
          if (data) {
            setInstanceData(data);
            setAssistantTitle(data.title || data.name || "Chat");
          }
        };
        loadInstanceData();
      }
    },
    onError: (error) => {
      toast({
        title: "Error saving settings",
        description: error.message || "Failed to update assistant settings.",
        variant: "destructive",
      });
    },
  });

  // Mutation for updating system assistant (Super Admin or Org Owner)
  const updateSystemAssistantMutation = useMutation({
    mutationFn: async (updates: any) => {
      if (!baseAssistant) {
        throw new Error("No assistant to update");
      }
      const result = await aiAssistantService.updateSystemAssistant(
        baseAssistant.id,
        updates,
      );
      if (result.error) {
        throw result.error;
      }
      return result.data;
    },
    onSuccess: () => {
      // Invalidate all related queries to ensure UI updates
      queryClient.invalidateQueries({
        queryKey: ["aiAssistant", baseAssistant?.id],
      });
      queryClient.invalidateQueries({
        queryKey: ["aiAssistantInstance", instanceId],
      });
      queryClient.invalidateQueries({
        queryKey: ["systemAssistants"],
      });

      setShowAssistantEditModal(false);
      toast({
        title: "Success",
        description: "Assistant updated successfully",
      });
    },
    onError: (error) => {
      toast({
        title: "Error updating assistant",
        description: error.message || "Failed to update assistant.",
        variant: "destructive",
      });
    },
  });

  // Convert markdown to HTML for rich text copying
  const markdownToHtml = (markdown: string): string => {
    let html = markdown
      // Remove horizontal rules first
      .replace(/^---+$/gm, "<hr>")
      // Convert headers (handle #### and other levels)
      .replace(/^#### (.*$)/gm, "<h4>$1</h4>")
      .replace(/^### (.*$)/gm, "<h3>$1</h3>")
      .replace(/^## (.*$)/gm, "<h2>$1</h2>")
      .replace(/^# (.*$)/gm, "<h1>$1</h1>")
      // Convert bold and italic
      .replace(/\*\*([^*]+)\*\*/g, "<strong>$1</strong>")
      .replace(/\*([^*]+)\*/g, "<em>$1</em>")
      // Convert links
      .replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2">$1</a>')
      // Convert inline code
      .replace(/`([^`]+)`/g, "<code>$1</code>")
      // Convert code blocks
      .replace(/```([\s\S]*?)```/g, "<pre><code>$1</code></pre>")
      // Convert unordered lists (handle bullet points and dashes)
      .replace(/^[\s]*[-•*+]\s+(.*$)/gm, "<li>$1</li>")
      // Convert numbered lists
      .replace(/^[\s]*\d+\.\s+(.*$)/gm, "<li>$1</li>");

    // Process lists properly - wrap consecutive list items
    html = html.replace(/(<li>.*?<\/li>[\s\n]*)+/gs, (match) => {
      // Check if this is part of a numbered list by looking for digits
      const hasNumbers = /^\s*\d+\./.test(match.replace(/<\/?li>/g, ""));
      const listType = hasNumbers ? "ol" : "ul";
      return `<${listType}>${match}</${listType}>`;
    });

    // Convert paragraphs and line breaks
    html = html
      // Split by double newlines for paragraphs
      .split(/\n\s*\n/)
      .map((paragraph) => {
        // Don't wrap block elements in <p> tags
        if (paragraph.match(/^<(h[1-6]|ul|ol|pre|hr|div)/)) {
          return paragraph;
        }
        // Convert single newlines to <br> within paragraphs
        return `<p>${paragraph.replace(/\n/g, "<br>")}</p>`;
      })
      .join("\n");

    // Clean up extra whitespace and empty paragraphs
    html = html
      .replace(/<p>\s*<\/p>/g, "")
      .replace(/<p>(<[uo]l>)/g, "$1")
      .replace(/(<\/[uo]l>)<\/p>/g, "$1")
      .replace(/<p>(<h[1-6]>)/g, "$1")
      .replace(/(<\/h[1-6]>)<\/p>/g, "$1")
      .replace(/<p>(<hr>)<\/p>/g, "$1")
      .trim();

    return html;
  };

  // Convert markdown to plain text
  const markdownToPlainText = (markdown: string): string => {
    return (
      markdown
        // Remove headers
        .replace(/^#{1,6}\s+/gm, "")
        // Remove bold and italic
        .replace(/\*\*([^*]+)\*\*/g, "$1")
        .replace(/\*([^*]+)\*/g, "$1")
        // Remove links, keep text
        .replace(/\[([^\]]+)\]\([^)]+\)/g, "$1")
        // Remove code blocks
        .replace(/```[\s\S]*?```/g, "")
        // Remove inline code
        .replace(/`([^`]+)`/g, "$1")
        // Remove list markers
        .replace(/^[\s]*[-*+]\s+/gm, "• ")
        // Remove numbered list markers
        .replace(/^[\s]*\d+\.\s+/gm, "")
        // Clean up extra whitespace
        .replace(/\n{3,}/g, "\n\n")
        .trim()
    );
  };

  // Copy to clipboard function with rich text support
  const copyToClipboard = async (text: string) => {
    try {
      const htmlContent = markdownToHtml(text);
      const plainText = markdownToPlainText(text);

      // Create a ClipboardItem with both HTML and plain text
      const clipboardItem = new ClipboardItem({
        "text/html": new Blob([htmlContent], { type: "text/html" }),
        "text/plain": new Blob([plainText], { type: "text/plain" }),
      });

      await navigator.clipboard.write([clipboardItem]);

      toast({
        title: "Copied to clipboard",
        description:
          "The message has been copied with formatting to your clipboard.",
      });
    } catch (err) {
      console.error(
        "Failed to copy rich text, falling back to plain text: ",
        err,
      );
      // Fallback to plain text if rich text copying fails
      try {
        const plainText = markdownToPlainText(text);
        await navigator.clipboard.writeText(plainText);
        toast({
          title: "Copied to clipboard",
          description: "The message has been copied to your clipboard.",
        });
      } catch (fallbackErr) {
        console.error("Failed to copy text: ", fallbackErr);
        toast({
          title: "Copy failed",
          description: "Failed to copy the message to clipboard.",
          variant: "destructive",
        });
      }
    }
  };

  // Get organization brand color
  const brandColor = organization?.brand_color || "#060520";

  // Helper function for friendly date formatting
  const formatFriendlyDate = (dateString: string) => {
    const date = new Date(dateString);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    // Reset time to compare dates only
    const dateOnly = new Date(
      date.getFullYear(),
      date.getMonth(),
      date.getDate(),
    );
    const todayOnly = new Date(
      today.getFullYear(),
      today.getMonth(),
      today.getDate(),
    );
    const yesterdayOnly = new Date(
      yesterday.getFullYear(),
      yesterday.getMonth(),
      yesterday.getDate(),
    );

    if (dateOnly.getTime() === todayOnly.getTime()) {
      return "Today";
    } else if (dateOnly.getTime() === yesterdayOnly.getTime()) {
      return "Yesterday";
    } else {
      // Format as "Sept 15" or "Jan 3"
      return date.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
      });
    }
  };

  // Thread title editing functions
  const startEditingThreadTitle = () => {
    if (!selectedThreadId) return;
    const currentThread = threads.find((t) => t.id === selectedThreadId);
    setEditingThreadTitle(currentThread?.title || "");
    setIsEditingThreadTitle(true);
  };

  const saveThreadTitle = async () => {
    if (!selectedThreadId || !user?.id) return;

    try {
      const { error } = await supabase
        .from("chat_threads")
        .update({ title: editingThreadTitle.trim() || null })
        .eq("id", selectedThreadId)
        .eq("user_id", user.id);

      if (error) throw error;

      // Update local threads state
      setThreads((prev) =>
        prev.map((t) =>
          t.id === selectedThreadId
            ? { ...t, title: editingThreadTitle.trim() || null }
            : t,
        ),
      );

      setIsEditingThreadTitle(false);

      toast({
        title: "Thread updated",
        description: "Thread title has been saved successfully.",
      });
    } catch (error: any) {
      console.error("Error updating thread title:", error);
      toast({
        title: "Error",
        description: "Failed to update thread title. Please try again.",
        variant: "destructive",
      });
    }
  };

  const cancelEditingThreadTitle = () => {
    setIsEditingThreadTitle(false);
    setEditingThreadTitle("");
  };

  const archiveThread = async (threadId: string) => {
    if (!user?.id) return;

    setIsArchiving(true);
    try {
      const { error } = await chatThreadsService.archiveThread(threadId);

      if (error) {
        console.error("Failed to archive thread:", error);
        toast({
          title: "Error",
          description: "Failed to archive conversation. Please try again.",
          variant: "destructive",
        });
        return;
      }

      // Show success message
      toast({
        title: "Conversation Archived",
        description: "The conversation has been archived successfully.",
      });

      // If we archived the currently selected thread, clear selection
      if (selectedThreadId === threadId) {
        setSelectedThreadId(null);
        setMessages([]);
      }

      // Reload threads to remove the archived one
      await reloadThreads();
    } catch (error) {
      console.error("Error archiving thread:", error);
      toast({
        title: "Error",
        description: "Failed to archive conversation. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsArchiving(false);
      setThreadToArchive(null);
    }
  };

  const toggleThreadVisibility = async (
    threadId: string,
    currentIsPublic: boolean,
  ) => {
    if (!user?.id) return;

    try {
      const { error } = await chatThreadsService.toggleThreadVisibility(
        threadId,
        !currentIsPublic,
      );

      if (error) {
        console.error("Failed to toggle thread visibility:", error);
        toast({
          title: "Error",
          description:
            "Failed to update conversation visibility. Please try again.",
          variant: "destructive",
        });
        return;
      }

      // Show success message
      toast({
        title: !currentIsPublic
          ? "Conversation Made Public"
          : "Conversation Made Private",
        description: !currentIsPublic
          ? "This conversation is now visible to your team."
          : "This conversation is now private.",
      });

      // Update the current thread state if it's the selected one
      if (selectedThreadId === threadId) {
        setCurrentThread((prev: any) =>
          prev ? { ...prev, is_public: !currentIsPublic } : null,
        );
      }

      // Reload threads to update the list
      await reloadThreads();
    } catch (error) {
      console.error("Error toggling thread visibility:", error);
      toast({
        title: "Error",
        description:
          "Failed to update conversation visibility. Please try again.",
        variant: "destructive",
      });
    }
  };

  return (
    <MainLayout>
      <div className="min-h-[100dvh] flex flex-col">
        {previewImageUrl && (
          <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/70"
            onClick={() => setPreviewImageUrl(null)}
          >
            <div
              className="relative max-w-4xl max-h-[85vh]"
              onClick={(e) => e.stopPropagation()}
            >
              <img
                src={previewImageUrl}
                alt={previewImageName || "Preview"}
                className="max-w-full max-h-[85vh] rounded-lg shadow-2xl"
              />
              <div className="absolute top-2 right-2 flex gap-2">
                <a
                  href={previewImageUrl}
                  download={previewImageName || "image.png"}
                  className="bg-white/95 hover:bg-white border border-gray-200 rounded p-2 shadow"
                  title="Download"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 24 24"
                    fill="currentColor"
                    className="w-5 h-5 text-gray-700"
                  >
                    <path
                      fillRule="evenodd"
                      d="M12 3.75a.75.75 0 01.75.75v8.19l2.47-2.47a.75.75 0 111.06 1.06l-3.75 3.75a.75.75 0 01-1.06 0l-3.75-3.75a.75.75 0 111.06-1.06l2.47 2.47V4.5A.75.75 0 0112 3.75z"
                      clipRule="evenodd"
                    />
                    <path d="M3 15.75A2.25 2.25 0 015.25 18h13.5A2.25 2.25 0 0021 15.75v-.75a.75.75 0 011.5 0v.75A3.75 3.75 0 0118.75 19.5H5.25A3.75 3.75 0 011.5 15.75v-.75a.75.75 0 011.5 0v.75z" />
                  </svg>
                </a>
                <button
                  type="button"
                  onClick={() => setPreviewImageUrl(null)}
                  className="bg-white/95 hover:bg-white border border-gray-200 rounded p-2 shadow"
                  title="Close"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 24 24"
                    fill="currentColor"
                    className="w-5 h-5 text-gray-700"
                  >
                    <path
                      fillRule="evenodd"
                      d="M6.225 4.811a.75.75 0 011.06 0L12 9.525l4.715-4.714a.75.75 0 111.06 1.06L13.06 10.586l4.715 4.714a.75.75 0 11-1.06 1.06L12 11.646l-4.715 4.714a.75.75 0 11-1.06-1.06l4.714-4.715-4.714-4.715a.75.75 0 010-1.059z"
                      clipRule="evenodd"
                    />
                  </svg>
                </button>
              </div>
            </div>
          </div>
        )}
        {/* Main Content Area */}
        <div className="flex flex-1 min-h-0 relative">
          {/* Mobile Overlay */}
          {isMobileSidebarOpen && (
            <div
              className="fixed inset-0 bg-black/50 z-40 md:hidden"
              onClick={() => setIsMobileSidebarOpen(false)}
            />
          )}

          {/* Conversations Sidebar */}
          <div
            className={cn(
              "w-72 border-r flex flex-col transition-transform duration-300 ease-in-out z-50",
              "bg-gray-50/50 md:bg-gray-50/50",
              "md:relative md:translate-x-0",
              isMobileSidebarOpen
                ? "fixed top-0 left-0 h-full translate-x-0 bg-white"
                : "fixed top-0 -translate-x-full md:translate-x-0",
            )}
          >
            {/* Mobile Header with Back and Close buttons */}
            <div className="md:hidden flex justify-between items-center p-4 border-b">
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate("/ai-assistants")}
                className="border-none bg-transparent hover:bg-gray-100"
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setIsMobileSidebarOpen(false)}
              >
                <X className="h-4 w-4" />
              </Button>
            </div>

            {/* Assistant Banner Section */}
            {baseAssistant && (
              <div className="bg-white border-b">
                <div className="relative">
                  {/* Banner */}
                  <div className="h-24 w-full relative overflow-hidden">
                    {baseAssistant.banner ? (
                      <div
                        className="h-full w-full bg-cover bg-center bg-no-repeat"
                        style={{
                          backgroundImage: `url(${baseAssistant.banner})`,
                        }}
                      />
                    ) : (
                      <div
                        className="h-full w-full"
                        style={{
                          background: `linear-gradient(135deg, ${brandColor}20, ${brandColor}40)`,
                        }}
                      />
                    )}
                  </div>

                  {/* Icon overlapping banner */}
                  <div className="absolute left-4" style={{ top: "66px" }}>
                    <div
                      className="bg-white rounded-full"
                      style={{ padding: "0.1rem" }}
                    >
                      <SmartIcon
                        iconUrl={baseAssistant?.icon_url}
                        size={52}
                        fallbackIcon={<Bot className="h-5 w-5" />}
                        alt={`${baseAssistant?.title || "Assistant"} icon`}
                      />
                    </div>
                  </div>

                  {/* Assistant Info */}
                  <div className="pt-8 px-4 pb-4">
                    <h2 className="text-sm font-semibold text-slate-800 mb-1">
                      {baseAssistant?.title || "AI Assistant"}
                    </h2>
                    <p className="text-xs text-slate-500 leading-relaxed">
                      {baseAssistant?.name}
                    </p>
                  </div>
                </div>
              </div>
            )}

            <div className="p-4 bg-white">
              <Button
                onClick={createNewThread}
                className="w-full flex items-center gap-2 bg-gray-900 hover:bg-gray-800 text-white mb-3"
              >
                <Plus className="h-4 w-4" />
                New Chat
              </Button>

              {/* View Mode Tabs */}
              <div className="flex rounded-md bg-gray-100 p-1 mb-3">
                <button
                  onClick={() => setViewMode("my")}
                  className={cn(
                    "flex-1 rounded-md px-3 py-1.5 text-sm font-medium transition-colors",
                    viewMode === "my"
                      ? "bg-white text-gray-900 shadow-sm"
                      : "text-gray-600 hover:text-gray-900",
                  )}
                >
                  My Chats
                </button>
                <button
                  onClick={() => setViewMode("team")}
                  className={cn(
                    "flex-1 rounded-md px-3 py-1.5 text-sm font-medium transition-colors",
                    viewMode === "team"
                      ? "bg-white text-gray-900 shadow-sm"
                      : "text-gray-600 hover:text-gray-900",
                  )}
                >
                  Team
                </button>
              </div>
            </div>

            <ScrollArea className="flex-1 p-2 -mt-2">
              <div className="space-y-0.5">
                {threads.map((t) => (
                  <div
                    key={t.id}
                    className={cn(
                      "cursor-pointer transition-colors rounded-md px-3 py-2 border-0 group relative",
                      selectedThreadId === t.id
                        ? ""
                        : "hover:bg-gray-50 text-gray-900",
                    )}
                    style={
                      selectedThreadId === t.id
                        ? {
                            backgroundColor: `color-mix(in srgb, ${brandColor} 15%, white)`,
                            color: "inherit",
                            border: "1px solid rgba(0,0,0,0.1)",
                          }
                        : {}
                    }
                    onClick={async () => {
                      setSelectedThreadId(t.id);
                      await loadMessages(t.id);
                    }}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-normal truncate">
                          {t.title && t.title.length > 17
                            ? `${t.title.slice(0, 17)}…`
                            : t.title || "New Conversation"}
                        </div>
                      </div>
                      {/* Date - hidden on hover */}
                      <div className="text-xs text-gray-500 ml-2 flex-shrink-0 group-hover:hidden">
                        {formatFriendlyDate(t.updated_at)}
                      </div>

                      {/* Archive button - shown on hover */}
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setThreadToArchive(t.id);
                        }}
                        className="hidden group-hover:flex items-center justify-center w-6 h-6 rounded-md hover:bg-gray-200 text-gray-500 hover:text-gray-700 ml-2 flex-shrink-0"
                        title="Archive conversation"
                      >
                        <Archive className="h-3 w-3" />
                      </button>
                    </div>
                  </div>
                ))}
                {threads.length === 0 && (
                  <div className="text-center py-8 text-gray-500">
                    <MessageSquare className="h-8 w-8 mx-auto mb-2 text-gray-300" />
                    <p className="text-sm">No conversations yet</p>
                    <p className="text-xs">Start your first chat!</p>
                  </div>
                )}
              </div>
            </ScrollArea>
          </div>

          {/* Main Chat Area */}
          <div
            className="flex-1 flex flex-col relative overflow-hidden md:ml-0"
            style={{
              backgroundColor:
                organization?.chat_background &&
                organization.chat_background !== "default" &&
                organization.chat_background !== "Default - Linear Background"
                  ? `${brandColor}08`
                  : `${brandColor}08`,
              minHeight: "100%",
            }}
          >
            {/* Chat Header */}
            <div
              className="sticky top-0 backdrop-blur-sm border-b px-6 py-2 flex items-center justify-between flex-shrink-0 z-20 bg-white"
              style={{
                borderBottomColor: "rgba(0,0,0,0.1)",
              }}
            >
              <div className="flex items-center gap-3">
                {/* Mobile Hamburger Menu */}
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setIsMobileSidebarOpen(true)}
                  className="md:hidden flex items-center gap-2"
                >
                  <Menu className="h-4 w-4" />
                </Button>

                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => window.history.back()}
                  className="flex items-center gap-2"
                >
                  <ArrowLeft className="h-4 w-4" />
                </Button>

                <div className="h-6 w-px bg-gray-300" />

                <div className="flex items-center gap-2">
                  <MessageSquare className="h-5 w-5 text-gray-600" />
                  {isEditingThreadTitle ? (
                    <div className="flex items-center gap-2">
                      <Input
                        value={editingThreadTitle}
                        onChange={(e) => setEditingThreadTitle(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            e.preventDefault();
                            saveThreadTitle();
                          } else if (e.key === "Escape") {
                            cancelEditingThreadTitle();
                          }
                        }}
                        onBlur={saveThreadTitle}
                        placeholder="Thread title"
                        className="h-8 px-2 text-sm font-medium min-w-[200px]"
                        autoFocus
                      />
                    </div>
                  ) : (
                    <div className="flex items-center gap-2 group">
                      <span
                        className={`font-medium transition-colors ${
                          selectedThreadId
                            ? "cursor-pointer hover:opacity-80"
                            : "cursor-default"
                        }`}
                        onClick={
                          selectedThreadId ? startEditingThreadTitle : undefined
                        }
                        title={
                          selectedThreadId ? "Click to edit thread title" : ""
                        }
                      >
                        {selectedThreadId
                          ? threads.find((t) => t.id === selectedThreadId)
                              ?.title || "New Conversation"
                          : "Select a conversation"}
                      </span>

                      {/* Visibility Toggle Icon */}
                      {selectedThreadId &&
                        currentThread &&
                        viewMode === "my" && (
                          <button
                            onClick={() =>
                              toggleThreadVisibility(
                                selectedThreadId,
                                currentThread.is_public,
                              )
                            }
                            className="ml-2 p-1 rounded-md hover:bg-gray-100 transition-colors"
                            title={
                              currentThread.is_public
                                ? "Make conversation private (visible only to you)"
                                : "Make conversation public (visible to your team)"
                            }
                          >
                            {currentThread.is_public ? (
                              <Eye className="h-4 w-4 text-gray-600" />
                            ) : (
                              <EyeOff className="h-4 w-4 text-gray-400" />
                            )}
                          </button>
                        )}
                      {selectedThreadId && (
                        <Edit
                          className="h-3 w-3 text-gray-400 cursor-pointer hover:text-gray-600 transition-colors"
                          onClick={startEditingThreadTitle}
                        />
                      )}
                    </div>
                  )}
                </div>
              </div>

              <div className="hidden md:flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleOpenEditAssistant}
                >
                  <Edit className="mr-2 h-4 w-4" /> Edit Assistant
                </Button>
                <Button variant="outline" size="sm">
                  <Settings className="mr-2 h-4 w-4" /> Settings
                </Button>
              </div>
            </div>
            {/* Custom Background Overlay */}
            {organization?.chat_background &&
              organization.chat_background !== "default" &&
              organization.chat_background !==
                "Default - Linear Background" && (
                <div
                  style={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    opacity: 0.06,
                    zIndex: 0,
                    pointerEvents: "none",
                    overflow: "hidden",
                    mixBlendMode: "multiply",
                  }}
                >
                  <img
                    src={
                      supabase.storage
                        .from("image_library")
                        .getPublicUrl(
                          `chat_backgrounds/${organization.chat_background}`,
                        ).data.publicUrl
                    }
                    alt=""
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                      objectPosition: "center center",
                      filter: `brightness(1.5) contrast(0.8)`,
                    }}
                  />
                </div>
              )}
            {!selectedThreadId ? (
              <div className="flex-1 flex items-center justify-center p-6">
                <div className="w-full max-w-md">
                  {isLoadingInstance || baseLoading ? (
                    <div className="text-center">
                      <div className="h-16 w-16 mx-auto mb-4 rounded-full bg-gray-100 flex items-center justify-center animate-pulse">
                        <Bot className="h-8 w-8 text-gray-400" />
                      </div>
                      <h3 className="text-xl font-semibold mb-2 text-slate-800">
                        Loading...
                      </h3>
                      <p className="text-slate-500 text-sm leading-relaxed">
                        Setting up your assistant
                      </p>
                    </div>
                  ) : (
                    <div className="text-center py-12">
                      {/* Assistant Icon */}
                      <div className="h-20 w-20 mx-auto mb-6 rounded-full bg-white border border-gray-200 flex items-center justify-center shadow-sm">
                        <SmartIcon
                          iconUrl={baseAssistant?.icon_url}
                          size={64}
                          fallbackIcon={
                            <Bot className="h-8 w-8 text-gray-600" />
                          }
                        />
                      </div>

                      {/* Assistant Title */}
                      <h1 className="text-2xl font-bold text-gray-900 mb-2">
                        {baseAssistant?.title || "Assistant"}
                      </h1>

                      {/* Assistant Name */}
                      <p className="text-gray-500 text-sm mb-6">
                        {baseAssistant?.name || "AI Assistant"}
                      </p>

                      {/* Start Chat Button */}
                      <div className="flex justify-center">
                        <Button
                          onClick={createNewThread}
                          className="flex items-center gap-2"
                          style={{ backgroundColor: brandColor }}
                        >
                          <MessageSquare className="h-4 w-4" />
                          Start chat
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div className="flex flex-col h-full relative bg-transparent z-[1] overflow-hidden">
                {/* Messages Area with full height and proper scrolling */}
                <div className="flex-1 min-h-0 relative bg-transparent overflow-hidden pb-4 md:pb-4">
                  <Conversation
                    key={selectedThreadId || "no-thread"}
                    className="h-full bg-transparent"
                  >
                    <ConversationContent className="p-6 pb-2 bg-transparent overflow-y-auto">
                      <div className="max-w-[768px] mx-auto space-y-6">
                        {displayMessages.map((m: any) => (
                          <div key={m.id}>
                            <Message from={m.role}>
                              <MessageContent
                                className={
                                  m.role === "assistant"
                                    ? "!bg-white border border-gray-200 shadow-sm"
                                    : ""
                                }
                                style={{
                                  borderRadius:
                                    m.role === "assistant"
                                      ? "1.2rem"
                                      : "1.2rem",
                                  ...(m.role === "user"
                                    ? {
                                        backgroundColor: `color-mix(in srgb, ${brandColor} 15%, white)`, // Light solid color
                                        color: "inherit", // Same as AI messages
                                        border: "1px solid rgba(0,0,0,0.1)",
                                      }
                                    : {}),
                                }}
                              >
                                {/* Render experimental_attachments first if present */}
                                {(m as any).experimental_attachments?.length >
                                  0 && (
                                  <div className="mb-3">
                                    {(m as any).experimental_attachments.map(
                                      (attachment: any, i: number) => {
                                        if (
                                          attachment.contentType?.startsWith(
                                            "image/",
                                          )
                                        ) {
                                          return (
                                            <div
                                              key={`attachment-${i}`}
                                              className="mt-2 mb-2"
                                            >
                                              <img
                                                src={attachment.url}
                                                alt={
                                                  attachment.name ||
                                                  `Attachment ${i + 1}`
                                                }
                                                className="max-w-full max-h-64 rounded-lg border border-gray-200 shadow-sm"
                                                style={{ maxWidth: "300px" }}
                                              />
                                            </div>
                                          );
                                        } else {
                                          // File card for PDFs and other files
                                          const fileName =
                                            attachment.name || "Attached file";
                                          const fileType =
                                            attachment.contentType ||
                                            "Unknown type";
                                          return (
                                            <div
                                              key={`attachment-${i}`}
                                              className="mt-2 mb-2 p-3 bg-gray-50 rounded-lg border border-gray-200 flex items-center gap-3"
                                            >
                                              <div className="flex-shrink-0 w-8 h-8 bg-blue-100 rounded flex items-center justify-center">
                                                <svg
                                                  className="w-4 h-4 text-blue-600"
                                                  fill="none"
                                                  stroke="currentColor"
                                                  viewBox="0 0 24 24"
                                                >
                                                  <path
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                    strokeWidth={2}
                                                    d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                                                  />
                                                </svg>
                                              </div>
                                              <div className="flex-1 min-w-0">
                                                <p className="text-sm font-medium text-gray-900 truncate">
                                                  {fileName}
                                                </p>
                                                <p className="text-xs text-gray-500">
                                                  {fileType}
                                                </p>
                                              </div>
                                              <a
                                                href={attachment.url}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="flex-shrink-0 text-blue-600 hover:text-blue-800 text-xs"
                                              >
                                                View
                                              </a>
                                            </div>
                                          );
                                        }
                                      },
                                    )}
                                  </div>
                                )}

                                {Array.isArray(m.parts) &&
                                m.parts.length > 0 ? (
                                  m.parts
                                    .filter((part: any) => {
                                      const t = part?.type;
                                      // Render only real content parts per AI SDK docs
                                      // Allowed: text, file/image, tool-call, tool-result, reasoning
                                      if (!t) return false;
                                      if (
                                        t === "text" ||
                                        t === "file" ||
                                        t === "image" ||
                                        t === "tool-call" ||
                                        t === "tool-result" ||
                                        t === "reasoning" ||
                                        t === "sources" ||
                                        (typeof t === "string" &&
                                          t.startsWith("tool-"))
                                      ) {
                                        return true;
                                      }
                                      // Drop stream lifecycle/meta parts like start, finish, start-step, finish-step, etc.
                                      return false;
                                    })
                                    .map((part: any, i: number) => {
                                      if (part.type === "text") {
                                        // Extract citations from other parts in this message
                                        const citations: Array<{
                                          url?: string;
                                          title?: string;
                                        }> = (() => {
                                          try {
                                            // tool-result citations
                                            const toolParts = (
                                              m.parts || []
                                            ).filter(
                                              (p: any) =>
                                                p?.type === "tool-result" &&
                                                p?.result?.citations,
                                            );
                                            if (toolParts.length > 0) {
                                              return toolParts.flatMap(
                                                (p: any) =>
                                                  p.result.citations || [],
                                              );
                                            }
                                            // sources parts
                                            const sourcesPart = (
                                              m.parts || []
                                            ).find(
                                              (p: any) =>
                                                p?.type === "sources" &&
                                                Array.isArray(p.sources),
                                            );
                                            if (sourcesPart)
                                              return sourcesPart.sources as any[];
                                          } catch {}
                                          return [];
                                        })();

                                        // Simple, stable rendering of text, then show citations below
                                        const displayText = part.text || "";
                                        return (
                                          <>
                                            {/* Show loading dots when waiting for first response */}
                                            {(m as any).isWaitingForResponse &&
                                            i === m.parts.length - 1 ? (
                                              <span className="inline-flex gap-1 text-gray-400">
                                                <span
                                                  className="animate-pulse"
                                                  style={{
                                                    animationDelay: "0ms",
                                                  }}
                                                >
                                                  ●
                                                </span>
                                                <span
                                                  className="animate-pulse"
                                                  style={{
                                                    animationDelay: "150ms",
                                                  }}
                                                >
                                                  ●
                                                </span>
                                                <span
                                                  className="animate-pulse"
                                                  style={{
                                                    animationDelay: "300ms",
                                                  }}
                                                >
                                                  ●
                                                </span>
                                              </span>
                                            ) : (
                                              <>
                                                <Response
                                                  key={`${m.id}-${i}-${part.state || "static"}`}
                                                  parseIncompleteMarkdown={true}
                                                  className={`${part.state === "streaming" ? "streaming" : ""} prose prose-sm max-w-none`}
                                                >
                                                  {part.state === "streaming"
                                                    ? (displayText || "")
                                                        .replace(/\*\*/g, "")
                                                        .replace(/\*/g, "")
                                                        .replace(/`/g, "")
                                                    : displayText}
                                                </Response>
                                                {/* Show typing cursor when content is streaming */}
                                                {(m as any).isStreaming &&
                                                  i === m.parts.length - 1 &&
                                                  displayText && (
                                                    <span className="inline-block w-[2px] h-4 ml-0.5 bg-gray-600 animate-pulse"></span>
                                                  )}
                                              </>
                                            )}
                                            {citations.length > 0 &&
                                              i === 0 && (
                                                <div className="px-1 pt-2">
                                                  <InlineCitation>
                                                    <InlineCitationCard>
                                                      <InlineCitationCardTrigger
                                                        sources={
                                                          citations
                                                            .map((c) => c.url)
                                                            .filter(
                                                              Boolean,
                                                            ) as string[]
                                                        }
                                                      />
                                                      <InlineCitationCardBody>
                                                        <InlineCitationCarousel>
                                                          <InlineCitationCarouselHeader>
                                                            <InlineCitationCarouselIndex />
                                                          </InlineCitationCarouselHeader>
                                                          <InlineCitationCarouselContent>
                                                            {citations.map(
                                                              (c, idx) => (
                                                                <InlineCitationCarouselItem
                                                                  key={idx}
                                                                >
                                                                  <InlineCitationSource
                                                                    title={
                                                                      c.title ||
                                                                      c.url
                                                                    }
                                                                    url={c.url}
                                                                  />
                                                                </InlineCitationCarouselItem>
                                                              ),
                                                            )}
                                                          </InlineCitationCarouselContent>
                                                        </InlineCitationCarousel>
                                                      </InlineCitationCardBody>
                                                    </InlineCitationCard>
                                                  </InlineCitation>
                                                  <Sources className="mt-2">
                                                    <SourcesTrigger
                                                      count={citations.length}
                                                    />
                                                    <SourcesContent>
                                                      {citations.map(
                                                        (c, idx) => (
                                                          <Source
                                                            key={idx}
                                                            href={c.url}
                                                            title={
                                                              c.title || c.url
                                                            }
                                                          />
                                                        ),
                                                      )}
                                                    </SourcesContent>
                                                  </Sources>
                                                </div>
                                              )}
                                          </>
                                        );
                                      }

                                      if (part.type === "file") {
                                        // Render file attachments using AI SDK v5 format
                                        // Support both 'url' (AI SDK v5) and 'data' (legacy) properties
                                        const mediaType =
                                          part.mediaType ||
                                          part.mimeType ||
                                          "application/octet-stream";

                                        // prefer `url` (v5 UIMessage spec), fallback to data/base64
                                        let fileUrl = part.url;
                                        if (
                                          !fileUrl &&
                                          typeof part.data === "string"
                                        ) {
                                          fileUrl = part.data.startsWith(
                                            "data:",
                                          )
                                            ? part.data
                                            : `data:${mediaType};base64,${part.data}`;
                                        }

                                        if (
                                          mediaType.startsWith("image/") &&
                                          fileUrl
                                        ) {
                                          const name =
                                            part.filename || `Image ${i + 1}`;
                                          return (
                                            <div
                                              key={`${m.id}-file-${i}`}
                                              className="mt-2 mb-2 relative group"
                                              style={{ maxWidth: "300px" }}
                                            >
                                              <button
                                                type="button"
                                                onClick={() => {
                                                  setPreviewImageUrl(fileUrl);
                                                  setPreviewImageName(name);
                                                }}
                                                className="block"
                                                title="Open preview"
                                              >
                                                <img
                                                  src={fileUrl}
                                                  alt={name}
                                                  className="max-w-full max-h-64 rounded-lg border border-gray-200 shadow-sm cursor-zoom-in"
                                                />
                                              </button>
                                              <a
                                                href={fileUrl}
                                                download={name}
                                                className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity bg-white/90 hover:bg-white border border-gray-200 rounded p-1 shadow"
                                                title="Download image"
                                              >
                                                <svg
                                                  xmlns="http://www.w3.org/2000/svg"
                                                  viewBox="0 0 24 24"
                                                  fill="currentColor"
                                                  className="w-4 h-4 text-gray-700"
                                                >
                                                  <path
                                                    fillRule="evenodd"
                                                    d="M12 3.75a.75.75 0 01.75.75v8.19l2.47-2.47a.75.75 0 111.06 1.06l-3.75 3.75a.75.75 0 01-1.06 0l-3.75-3.75a.75.75 0 111.06-1.06l2.47 2.47V4.5A.75.75 0 0112 3.75z"
                                                    clipRule="evenodd"
                                                  />
                                                  <path d="M3 15.75A2.25 2.25 0 015.25 18h13.5A2.25 2.25 0 0021 15.75v-.75a.75.75 0 011.5 0v.75A3.75 3.75 0 0118.75 19.5H5.25A3.75 3.75 0 011.5 15.75v-.75a.75.75 0 011.5 0v.75z" />
                                                </svg>
                                              </a>
                                            </div>
                                          );
                                        } else if (fileUrl) {
                                          // File card for PDFs and other files
                                          const fileName =
                                            part.filename || "Attached file";
                                          const fileType =
                                            part.mediaType || "Unknown type";
                                          return (
                                            <div
                                              key={`${m.id}-file-${i}`}
                                              className="mt-2 mb-2 p-3 bg-gray-50 rounded-lg border border-gray-200 flex items-center gap-3"
                                              style={{ maxWidth: "300px" }}
                                            >
                                              <div className="flex-shrink-0 w-8 h-8 bg-blue-100 rounded flex items-center justify-center">
                                                <svg
                                                  className="w-4 h-4 text-blue-600"
                                                  fill="none"
                                                  stroke="currentColor"
                                                  viewBox="0 0 24 24"
                                                >
                                                  <path
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                    strokeWidth={2}
                                                    d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                                                  />
                                                </svg>
                                              </div>
                                              <div className="flex-1 min-w-0">
                                                <p className="text-sm font-medium text-gray-900 truncate">
                                                  {fileName}
                                                </p>
                                                <p className="text-xs text-gray-500">
                                                  {fileType}
                                                </p>
                                              </div>
                                              <a
                                                href={fileUrl}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="flex-shrink-0 text-blue-600 hover:text-blue-800 text-xs"
                                              >
                                                View
                                              </a>
                                            </div>
                                          );
                                        }
                                      }

                                      if (
                                        part.type === "tool-call" ||
                                        part.type === "tool-result" ||
                                        part.type?.startsWith("tool-")
                                      ) {
                                        // Detect AI tool rich blocks
                                        const richPayload = (part.result ||
                                          part.output) as any;
                                        const isCustomBlocks =
                                          (richPayload &&
                                            typeof richPayload === "object" &&
                                            richPayload?.metadata
                                              ?.contentType ===
                                              "custom-blocks") ||
                                          (typeof richPayload === "string" &&
                                            /<\w+[^>]*>(.|\n)*<\/\w+>/.test(
                                              richPayload,
                                            ) &&
                                            /<(task|checklist|infobox|callout|action|highlight|table|quote)\b/i.test(
                                              richPayload,
                                            ));
                                        return (
                                          <Tool
                                            key={
                                              part.toolCallId || `${m.id}-${i}`
                                            }
                                            defaultOpen={false}
                                          >
                                            <div className="relative">
                                              <ToolHeader
                                                type={part.type}
                                                state={part.state}
                                              />
                                              {/* Expand (drawer) from header even when collapsed */}
                                              <div className="absolute right-2 top-1/2 -translate-y-1/2">
                                                <Sheet>
                                                  <SheetTrigger asChild>
                                                    <button
                                                      className="text-gray-500 hover:text-gray-800 text-xs inline-flex items-center gap-1"
                                                      title="Expand"
                                                      onClick={(e) =>
                                                        e.stopPropagation()
                                                      }
                                                    >
                                                      <Maximize2 className="h-3 w-3" />
                                                      Expand
                                                    </button>
                                                  </SheetTrigger>
                                                  <SheetContent
                                                    side="right"
                                                    className="w-[900px] max-w-[90vw]"
                                                  >
                                                    <div className="flex items-center justify-between py-2 pr-2">
                                                      <SheetTitle>
                                                        Tool output
                                                      </SheetTitle>
                                                      <SaveToolOutputButton
                                                        toolName={
                                                          part.type?.toString?.() ||
                                                          "tool"
                                                        }
                                                        assistantInstanceId={(() => {
                                                          const contentRaw =
                                                            (part.result as any) ||
                                                            (part.output as any) ||
                                                            "";
                                                          const contentStr =
                                                            typeof contentRaw ===
                                                            "string"
                                                              ? contentRaw
                                                              : "";
                                                          const match =
                                                            contentStr.match(
                                                              /<!-- TOOL_METADATA:(.+?) -->/,
                                                            );
                                                          try {
                                                            const meta = match
                                                              ? JSON.parse(
                                                                  match[1],
                                                                )
                                                              : null;
                                                            return (
                                                              meta?.generatorInstanceId ||
                                                              (
                                                                part.result as any
                                                              )
                                                                ?.generatorInstanceId ||
                                                              (
                                                                part.output as any
                                                              )
                                                                ?.generatorInstanceId ||
                                                              undefined
                                                            );
                                                          } catch {
                                                            return undefined;
                                                          }
                                                        })()}
                                                        outputId={(() => {
                                                          const contentRaw =
                                                            (part.result as any) ||
                                                            (part.output as any) ||
                                                            "";
                                                          const contentStr =
                                                            typeof contentRaw ===
                                                            "string"
                                                              ? contentRaw
                                                              : "";
                                                          const match =
                                                            contentStr.match(
                                                              /<!-- TOOL_METADATA:(.+?) -->/,
                                                            );
                                                          try {
                                                            const meta = match
                                                              ? JSON.parse(
                                                                  match[1],
                                                                )
                                                              : null;
                                                            return (
                                                              meta?.outputId ||
                                                              (
                                                                part.result as any
                                                              )?.outputId ||
                                                              (
                                                                part.output as any
                                                              )?.outputId ||
                                                              null
                                                            );
                                                          } catch {
                                                            return (
                                                              (
                                                                part.result as any
                                                              )?.outputId ||
                                                              (
                                                                part.output as any
                                                              )?.outputId ||
                                                              null
                                                            );
                                                          }
                                                        })()}
                                                      />
                                                    </div>
                                                    <div className="mt-4 overflow-y-auto h-[calc(100vh-8rem)] pr-2">
                                                      {isCustomBlocks ? (
                                                        <AIContentRenderer
                                                          content={
                                                            richPayload?.content ??
                                                            richPayload
                                                          }
                                                        />
                                                      ) : (
                                                        <div className="px-1">
                                                          {part.result ||
                                                          part.output ? (
                                                            typeof (
                                                              part.result ||
                                                              part.output
                                                            ) === "string" ? (
                                                              <div className="whitespace-pre-wrap text-sm">
                                                                {(
                                                                  part.result ||
                                                                  (part.output as any)
                                                                ).replace(
                                                                  /<!-- TOOL_METADATA:.+? -->\n?/,
                                                                  "",
                                                                )}
                                                              </div>
                                                            ) : (
                                                              <pre className="whitespace-pre-wrap text-xs">
                                                                {JSON.stringify(
                                                                  part.result ||
                                                                    part.output,
                                                                  null,
                                                                  2,
                                                                )}
                                                              </pre>
                                                            )
                                                          ) : null}
                                                        </div>
                                                      )}
                                                    </div>
                                                  </SheetContent>
                                                </Sheet>
                                              </div>
                                            </div>
                                            <ToolContent>
                                              {/* Show input for tool-call types */}
                                              {(part.type === "tool-call" ||
                                                part.input) && (
                                                <ToolInput input={part.input} />
                                              )}
                                              {/* Show output for tool-result types or legacy output */}
                                              {(part.type === "tool-result" ||
                                                part.output ||
                                                part.result) &&
                                                (isCustomBlocks ? (
                                                  <div className="my-2">
                                                    <AIContentRenderer
                                                      content={
                                                        richPayload?.content ??
                                                        richPayload
                                                      }
                                                    />
                                                  </div>
                                                ) : (
                                                  <>
                                                    {/* Citations rendering if present */}
                                                    {Array.isArray(
                                                      (part as any)?.result
                                                        ?.citations,
                                                    ) && (
                                                      <div className="px-4 pt-3">
                                                        {(
                                                          part as any
                                                        ).result.citations.map(
                                                          (
                                                            c: any,
                                                            idx: number,
                                                          ) => (
                                                            <InlineCitation
                                                              key={idx}
                                                            >
                                                              <InlineCitationText>
                                                                [{idx + 1}]
                                                              </InlineCitationText>
                                                              <InlineCitationCard>
                                                                <InlineCitationCardTrigger
                                                                  sources={[
                                                                    c.url,
                                                                  ].filter(
                                                                    Boolean,
                                                                  )}
                                                                />
                                                                <InlineCitationCardBody>
                                                                  <InlineCitationCarousel>
                                                                    <InlineCitationCarouselHeader>
                                                                      <InlineCitationCarouselIndex />
                                                                    </InlineCitationCarouselHeader>
                                                                    <InlineCitationCarouselContent>
                                                                      <InlineCitationCarouselItem>
                                                                        <InlineCitationSource
                                                                          title={
                                                                            c.title ||
                                                                            c.url
                                                                          }
                                                                          url={
                                                                            c.url
                                                                          }
                                                                          description={
                                                                            c.sourceType
                                                                          }
                                                                        />
                                                                      </InlineCitationCarouselItem>
                                                                    </InlineCitationCarouselContent>
                                                                  </InlineCitationCarousel>
                                                                </InlineCitationCardBody>
                                                              </InlineCitationCard>
                                                            </InlineCitation>
                                                          ),
                                                        )}
                                                        {/* Sources list */}
                                                        <Sources className="mt-2">
                                                          <SourcesTrigger
                                                            count={
                                                              (part as any)
                                                                .result
                                                                .citations
                                                                .length
                                                            }
                                                          />
                                                          <SourcesContent>
                                                            {(
                                                              part as any
                                                            ).result.citations.map(
                                                              (
                                                                c: any,
                                                                i: number,
                                                              ) => (
                                                                <Source
                                                                  key={i}
                                                                  href={c.url}
                                                                  title={
                                                                    c.title ||
                                                                    c.url
                                                                  }
                                                                />
                                                              ),
                                                            )}
                                                          </SourcesContent>
                                                        </Sources>
                                                      </div>
                                                    )}
                                                    <ToolOutput
                                                      output={
                                                        part.result ||
                                                        part.output ? (
                                                          typeof (
                                                            part.result ||
                                                            part.output
                                                          ) === "string" ? (
                                                            // Strip metadata comment from displayed content
                                                            (
                                                              part.result ||
                                                              part.output
                                                            ).replace(
                                                              /<!-- TOOL_METADATA:.+? -->\n?/,
                                                              "",
                                                            )
                                                          ) : (
                                                            <pre className="whitespace-pre-wrap text-xs">
                                                              {JSON.stringify(
                                                                part.result ||
                                                                  part.output,
                                                                null,
                                                                2,
                                                              )}
                                                            </pre>
                                                          )
                                                        ) : undefined
                                                      }
                                                      errorText={
                                                        part.errorText ||
                                                        (part.isError
                                                          ? "Tool execution failed"
                                                          : undefined)
                                                      }
                                                    />
                                                  </>
                                                ))}

                                              {/* Save tool output button */}
                                              {(part.type?.startsWith(
                                                "tool-",
                                              ) ||
                                                part.type === "tool-result") &&
                                                part.toolCallId &&
                                                (part.output ||
                                                  part.result) && (
                                                  <div className="px-4 pb-2 pt-2 border-t border-gray-100 flex items-center justify-between">
                                                    <SaveToolOutputButton
                                                      toolName={
                                                        part.type?.startsWith(
                                                          "tool-",
                                                        )
                                                          ? part.type.replace(
                                                              "tool-",
                                                              "",
                                                            )
                                                          : part.type
                                                      }
                                                      assistantInstanceId={(() => {
                                                        // Try to extract metadata from content string
                                                        const content =
                                                          (part.result as any) ||
                                                          (part.output as any) ||
                                                          "";
                                                        const metadataMatch = (
                                                          typeof content ===
                                                          "string"
                                                            ? content
                                                            : ""
                                                        ).match(
                                                          /<!-- TOOL_METADATA:(.+?) -->/,
                                                        );
                                                        let metadata = null;

                                                        if (metadataMatch) {
                                                          try {
                                                            metadata =
                                                              JSON.parse(
                                                                metadataMatch[1],
                                                              );
                                                          } catch (e) {
                                                            console.warn(
                                                              "Failed to parse tool metadata:",
                                                              e,
                                                            );
                                                          }
                                                        }

                                                        const instanceId =
                                                          metadata?.generatorInstanceId ||
                                                          (part.result as any)
                                                            ?.generatorInstanceId ||
                                                          (part.output as any)
                                                            ?.generatorInstanceId ||
                                                          undefined;

                                                        console.log(
                                                          "Debug SaveToolOutputButton props:",
                                                          {
                                                            partType: part.type,
                                                            partResult:
                                                              part.result,
                                                            partOutput:
                                                              part.output,
                                                            metadata,
                                                            instanceId,
                                                            outputId:
                                                              metadata?.outputId ||
                                                              (
                                                                part.result as any
                                                              )?.outputId ||
                                                              (
                                                                part.output as any
                                                              )?.outputId,
                                                          },
                                                        );

                                                        return instanceId;
                                                      })()}
                                                      outputId={(() => {
                                                        // Try to extract metadata from content string
                                                        const content =
                                                          (part.result as any) ||
                                                          (part.output as any) ||
                                                          "";
                                                        const metadataMatch = (
                                                          typeof content ===
                                                          "string"
                                                            ? content
                                                            : ""
                                                        ).match(
                                                          /<!-- TOOL_METADATA:(.+?) -->/,
                                                        );
                                                        let metadata = null;

                                                        if (metadataMatch) {
                                                          try {
                                                            metadata =
                                                              JSON.parse(
                                                                metadataMatch[1],
                                                              );
                                                          } catch (e) {
                                                            console.warn(
                                                              "Failed to parse tool metadata:",
                                                              e,
                                                            );
                                                          }
                                                        }

                                                        return (
                                                          metadata?.outputId ||
                                                          (part.result as any)
                                                            ?.outputId ||
                                                          (part.output as any)
                                                            ?.outputId ||
                                                          null
                                                        );
                                                      })()}
                                                    />

                                                    {/* Full-screen (drawer) view */}
                                                    <Sheet>
                                                      <SheetTrigger asChild>
                                                        <button
                                                          className="text-gray-500 hover:text-gray-800 text-xs inline-flex items-center gap-1"
                                                          title="Expand"
                                                        >
                                                          <Maximize2 className="h-3 w-3" />
                                                          Expand
                                                        </button>
                                                      </SheetTrigger>
                                                      <SheetContent
                                                        side="right"
                                                        className="w-[900px] max-w-[90vw]"
                                                      >
                                                        <div className="flex items-center justify-between py-2 pr-2">
                                                          <SheetTitle>
                                                            Tool output
                                                          </SheetTitle>
                                                          <SaveToolOutputButton
                                                            toolName={
                                                              part.type?.toString?.() ||
                                                              "tool"
                                                            }
                                                            assistantInstanceId={(() => {
                                                              const content =
                                                                (part.result as any) ||
                                                                (part.output as any) ||
                                                                "";
                                                              const match = (
                                                                typeof content ===
                                                                "string"
                                                                  ? content
                                                                  : ""
                                                              ).match(
                                                                /<!-- TOOL_METADATA:(.+?) -->/,
                                                              );
                                                              try {
                                                                const meta =
                                                                  match
                                                                    ? JSON.parse(
                                                                        match[1],
                                                                      )
                                                                    : null;
                                                                return (
                                                                  meta?.generatorInstanceId ||
                                                                  (
                                                                    part.result as any
                                                                  )
                                                                    ?.generatorInstanceId ||
                                                                  (
                                                                    part.output as any
                                                                  )
                                                                    ?.generatorInstanceId ||
                                                                  undefined
                                                                );
                                                              } catch {
                                                                return undefined;
                                                              }
                                                            })()}
                                                            outputId={(() => {
                                                              const content =
                                                                (part.result as any) ||
                                                                (part.output as any) ||
                                                                "";
                                                              const match = (
                                                                typeof content ===
                                                                "string"
                                                                  ? content
                                                                  : ""
                                                              ).match(
                                                                /<!-- TOOL_METADATA:(.+?) -->/,
                                                              );
                                                              try {
                                                                const meta =
                                                                  match
                                                                    ? JSON.parse(
                                                                        match[1],
                                                                      )
                                                                    : null;
                                                                return (
                                                                  meta?.outputId ||
                                                                  (
                                                                    part.result as any
                                                                  )?.outputId ||
                                                                  (
                                                                    part.output as any
                                                                  )?.outputId ||
                                                                  null
                                                                );
                                                              } catch {
                                                                return (
                                                                  (
                                                                    part.result as any
                                                                  )?.outputId ||
                                                                  (
                                                                    part.output as any
                                                                  )?.outputId ||
                                                                  null
                                                                );
                                                              }
                                                            })()}
                                                          />
                                                        </div>
                                                        <div className="mt-4 overflow-y-auto h-[calc(100vh-8rem)] pr-2">
                                                          {isCustomBlocks ? (
                                                            <AIContentRenderer
                                                              content={
                                                                richPayload?.content ??
                                                                richPayload
                                                              }
                                                            />
                                                          ) : (
                                                            <div className="px-1">
                                                              {part.result ||
                                                              part.output ? (
                                                                typeof (
                                                                  part.result ||
                                                                  part.output
                                                                ) ===
                                                                "string" ? (
                                                                  <div className="whitespace-pre-wrap text-sm">
                                                                    {(
                                                                      part.result ||
                                                                      (part.output as any)
                                                                    ).replace(
                                                                      /<!-- TOOL_METADATA:.+? -->\n?/,
                                                                      "",
                                                                    )}
                                                                  </div>
                                                                ) : (
                                                                  <pre className="whitespace-pre-wrap text-xs">
                                                                    {JSON.stringify(
                                                                      part.result ||
                                                                        part.output,
                                                                      null,
                                                                      2,
                                                                    )}
                                                                  </pre>
                                                                )
                                                              ) : null}
                                                            </div>
                                                          )}
                                                        </div>
                                                      </SheetContent>
                                                    </Sheet>
                                                  </div>
                                                )}
                                            </ToolContent>
                                          </Tool>
                                        );
                                      }

                                      if (part.type === "reasoning") {
                                        return (
                                          <Reasoning
                                            key={`${m.id}-${i}`}
                                            className="w-full"
                                            isStreaming={
                                              (status === "streaming" &&
                                                i === m.parts.length - 1 &&
                                                m.id ===
                                                  (messages as any)?.[
                                                    messages.length - 1
                                                  ]?.id) ||
                                              false
                                            }
                                          >
                                            <ReasoningTrigger />
                                            <ReasoningContent>
                                              {(part as any).text || ""}
                                            </ReasoningContent>
                                          </Reasoning>
                                        );
                                      }

                                      if (
                                        part.type === "sources" &&
                                        Array.isArray((part as any).sources)
                                      ) {
                                        const srcs = (part as any)
                                          .sources as Array<{
                                          url?: string;
                                          title?: string;
                                        }>;
                                        const urls = srcs
                                          .map((s) => s.url)
                                          .filter(Boolean) as string[];
                                        return (
                                          <div
                                            key={`${m.id}-sources-${i}`}
                                            className="px-4 pt-3"
                                          >
                                            {/* Inline pill with aggregated sources */}
                                            {urls.length > 0 && (
                                              <InlineCitation>
                                                <InlineCitationCard>
                                                  <InlineCitationCardTrigger
                                                    sources={urls}
                                                  />
                                                  <InlineCitationCardBody>
                                                    <InlineCitationCarousel>
                                                      <InlineCitationCarouselHeader>
                                                        <InlineCitationCarouselIndex />
                                                      </InlineCitationCarouselHeader>
                                                      <InlineCitationCarouselContent>
                                                        {srcs.map((c, idx) => (
                                                          <InlineCitationCarouselItem
                                                            key={idx}
                                                          >
                                                            <InlineCitationSource
                                                              title={
                                                                c.title || c.url
                                                              }
                                                              url={c.url}
                                                            />
                                                          </InlineCitationCarouselItem>
                                                        ))}
                                                      </InlineCitationCarouselContent>
                                                    </InlineCitationCarousel>
                                                  </InlineCitationCardBody>
                                                </InlineCitationCard>
                                              </InlineCitation>
                                            )}
                                            {/* Full sources list */}
                                            <Sources className="mt-2">
                                              <SourcesTrigger
                                                count={srcs.length}
                                              />
                                              <SourcesContent>
                                                {srcs.map((c, idx) => (
                                                  <Source
                                                    key={idx}
                                                    href={c.url}
                                                    title={c.title || c.url}
                                                  />
                                                ))}
                                              </SourcesContent>
                                            </Sources>
                                          </div>
                                        );
                                      }

                                      // Handle image attachments
                                      if (part.type === "image" && part.image) {
                                        return (
                                          <div
                                            key={`${m.id}-${i}`}
                                            className="mt-2 mb-2"
                                          >
                                            <img
                                              src={part.image}
                                              alt="Attached image"
                                              className="max-w-full max-h-64 rounded-lg border border-gray-200 shadow-sm"
                                              style={{ maxWidth: "300px" }}
                                            />
                                          </div>
                                        );
                                      }

                                      // Handle file attachments
                                      if (part.type === "file" && part.data) {
                                        const fileName =
                                          part.name || "Attached file";
                                        const fileType =
                                          part.mediaType || "Unknown type";
                                        return (
                                          <div
                                            key={`${m.id}-${i}`}
                                            className="mt-2 mb-2 p-3 bg-gray-50 rounded-lg border border-gray-200 flex items-center gap-3"
                                          >
                                            <div className="flex-shrink-0 w-8 h-8 bg-gray-300 rounded flex items-center justify-center">
                                              <svg
                                                className="w-4 h-4 text-gray-600"
                                                fill="none"
                                                stroke="currentColor"
                                                viewBox="0 0 24 24"
                                              >
                                                <path
                                                  strokeLinecap="round"
                                                  strokeLinejoin="round"
                                                  strokeWidth={2}
                                                  d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                                                />
                                              </svg>
                                            </div>
                                            <div className="flex-1 min-w-0">
                                              <p className="text-sm font-medium text-gray-900 truncate">
                                                {fileName}
                                              </p>
                                              <p className="text-xs text-gray-500">
                                                {fileType}
                                              </p>
                                            </div>
                                          </div>
                                        );
                                      }

                                      // Handle error parts
                                      if (
                                        part.type === "error" &&
                                        (part as any).error
                                      ) {
                                        return (
                                          <div
                                            key={`${m.id}-error-${i}`}
                                            className="mt-2 mb-2 p-3 bg-red-50 rounded-lg border border-red-200"
                                          >
                                            <div className="flex items-start gap-2">
                                              <div className="flex-shrink-0 w-5 h-5 text-red-600">
                                                <svg
                                                  fill="none"
                                                  stroke="currentColor"
                                                  viewBox="0 0 24 24"
                                                >
                                                  <path
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                    strokeWidth={2}
                                                    d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                                                  />
                                                </svg>
                                              </div>
                                              <div className="flex-1">
                                                <p className="text-sm font-medium text-red-800">
                                                  Stream Error
                                                </p>
                                                <p className="text-xs text-red-600 mt-1">
                                                  {(part as any).error}
                                                </p>
                                              </div>
                                            </div>
                                          </div>
                                        );
                                      }

                                      // Fallback for unknown part types (future-proofing)
                                      if (part.type && part.type !== "text") {
                                        return (
                                          <div
                                            key={`${m.id}-unknown-${i}`}
                                            className="mt-2 mb-2 p-3 bg-yellow-50 rounded-lg border border-yellow-200"
                                          >
                                            <div className="flex items-start gap-2">
                                              <div className="flex-shrink-0 w-5 h-5 text-yellow-600">
                                                <svg
                                                  fill="none"
                                                  stroke="currentColor"
                                                  viewBox="0 0 24 24"
                                                >
                                                  <path
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                    strokeWidth={2}
                                                    d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                                                  />
                                                </svg>
                                              </div>
                                              <div className="flex-1">
                                                <p className="text-sm font-medium text-yellow-800">
                                                  Unknown content type:{" "}
                                                  {part.type}
                                                </p>
                                                <details className="mt-2">
                                                  <summary className="text-xs text-yellow-600 cursor-pointer">
                                                    Show raw data
                                                  </summary>
                                                  <pre className="mt-2 text-xs text-yellow-700 whitespace-pre-wrap">
                                                    {JSON.stringify(
                                                      part,
                                                      null,
                                                      2,
                                                    )}
                                                  </pre>
                                                </details>
                                              </div>
                                            </div>
                                          </div>
                                        );
                                      }

                                      return null;
                                    })
                                ) : (
                                  <Response parseIncompleteMarkdown={true}>
                                    {(m as any).content ?? ""}
                                  </Response>
                                )}
                              </MessageContent>
                            </Message>

                            {/* Copy and Save Actions for Assistant Messages - Only show when streaming is done */}
                            {m.role === "assistant" &&
                              status !== "streaming" &&
                              status !== "submitted" &&
                              (() => {
                                const textContent =
                                  Array.isArray(m.parts) && m.parts.length > 0
                                    ? m.parts.find(
                                        (part: any) => part.type === "text",
                                      )?.text || ""
                                    : ((m as any).content ?? "");

                                return (
                                  <div className="flex justify-start ml-2 -mt-1">
                                    <Actions>
                                      <Action
                                        tooltip="Copy message"
                                        onClick={() => {
                                          copyToClipboard(textContent);
                                        }}
                                      >
                                        <Copy className="h-4 w-4" />
                                      </Action>
                                      <SaveChatMessageButton
                                        messageContent={textContent}
                                        assistantId={
                                          instanceData?.ai_assistant_id || ""
                                        }
                                        assistantInstanceId={instanceId || ""}
                                        messageTitle={`Chat Response ${new Date().toLocaleDateString()}`}
                                        messageId={m.id}
                                        className="ml-1"
                                      />
                                    </Actions>
                                  </div>
                                );
                              })()}
                          </div>
                        ))}

                        {messages.length === 0 &&
                          status === "ready" &&
                          selectedThreadId && (
                            <div className="text-center py-12">
                              <div
                                className="h-16 w-16 mx-auto mb-4 rounded-full bg-white border flex items-center justify-center overflow-hidden"
                                style={{ padding: "0.15rem" }}
                              >
                                <SmartIcon
                                  iconUrl={
                                    instanceData?.icon_url ||
                                    baseAssistant?.icon_url ||
                                    null
                                  }
                                  size={60}
                                  fallbackIcon={<Bot className="h-6 w-6" />}
                                  alt={`${instanceData?.title || baseAssistant?.title || "Assistant"} icon`}
                                />
                              </div>
                              <h1 className="text-2xl font-bold mb-2">
                                <span style={{ color: brandColor }}>
                                  Hi{" "}
                                  {userProfile?.first_name ||
                                    user?.user_metadata?.first_name ||
                                    user?.user_metadata?.name?.split(" ")[0] ||
                                    "there"}
                                </span>
                                , how can I help you today?
                              </h1>
                              <p className="text-gray-500">
                                {(baseAssistant as any)?.chat_welcome_message ||
                                  "Send a message to begin your conversation."}
                              </p>
                            </div>
                          )}
                      </div>
                    </ConversationContent>
                    <ConversationScrollButton />
                  </Conversation>
                </div>

                {/* Message Input - Fixed at bottom with absolute positioning */}
                <div className="sticky bottom-0 left-0 right-0 p-4 pb-4 z-20 bg-gradient-to-t from-white via-white/90 to-transparent">
                  <div className="max-w-[768px] mx-auto relative">
                    <PromptInput
                      onSubmit={(e) => {
                        e.preventDefault();
                        submitMessage();
                      }}
                      className="relative border border-gray-200 rounded-xl"
                    >
                      <PromptInputTextarea
                        value={localInput}
                        onChange={(e) => setLocalInput(e.target.value)}
                        autoFocus
                        minHeight={60}
                        maxHeight={240}
                        onKeyDown={(e) => {
                          if (
                            e.key === "Enter" &&
                            !e.shiftKey &&
                            !(e as any).nativeEvent?.isComposing
                          ) {
                            e.preventDefault();
                            submitMessage();
                          }
                        }}
                        onPaste={async (e) => {
                          // Handle image paste from clipboard
                          const clipboardData = e.clipboardData;
                          if (clipboardData && clipboardData.items) {
                            const items = Array.from(clipboardData.items);
                            const imageItems = items.filter(
                              (item) =>
                                item.kind === "file" &&
                                item.type.startsWith("image/"),
                            );

                            if (imageItems.length > 0) {
                              e.preventDefault(); // Prevent default paste behavior for images

                              for (const item of imageItems) {
                                const file = item.getAsFile();
                                if (file) {
                                  // Upload the pasted image to Supabase Storage
                                  const uploadedFile =
                                    await uploadFileToStorage(file);
                                  if (uploadedFile) {
                                    // Add the uploaded file to attached files
                                    setAttachedFiles((prev) => [
                                      ...prev,
                                      uploadedFile,
                                    ]);
                                  }
                                }
                              }
                            }
                          }
                        }}
                        placeholder={
                          attachedFiles.length > 0
                            ? `${attachedFiles.length} file(s) attached. Type your message...`
                            : "Type your message..."
                        }
                        disabled={
                          status === "submitted" || status === "streaming"
                        }
                        className="focus:outline-none focus:ring-0 focus:shadow-none transition-all duration-200"
                      />

                      {/* File Preview Cards - Inside Input */}
                      {attachedFiles.length > 0 && (
                        <div className="px-3 py-2 flex flex-wrap gap-2 border-t border-gray-100">
                          {attachedFiles.map((file, index) => (
                            <div
                              key={index}
                              className="flex items-center gap-2 px-2 py-1 bg-gray-50 rounded-md border border-gray-200 text-xs max-w-[180px]"
                            >
                              <div className="flex-shrink-0">
                                {file.type?.startsWith("image/") ? (
                                  <ImageIcon className="h-3 w-3 text-blue-600" />
                                ) : (
                                  <FileText className="h-3 w-3 text-gray-600" />
                                )}
                              </div>
                              <span className="truncate text-gray-700 flex-1 text-xs">
                                {file.name || "Unknown file"}
                              </span>
                              <button
                                type="button"
                                onClick={() => removeFile(index)}
                                className="flex-shrink-0 text-gray-400 hover:text-red-500 transition-colors"
                              >
                                <X className="h-2 w-2" />
                              </button>
                            </div>
                          ))}
                        </div>
                      )}

                      <PromptInputToolbar>
                        <PromptInputTools>
                          {/* Enhanced Plus Button with Connectors Menu */}
                          <ConnectorsMenu
                            threadId={selectedThreadId}
                            selectedTools={selectedTools}
                            onToolsChange={setSelectedTools}
                            onConnect={(id) => {
                              console.log(`Connect ${id}`);
                              // Navigate to integrations page
                              window.open("/settings/integrations", "_blank");
                            }}
                          />

                          {/* File Upload Button with same style as ConnectorsMenu */}
                          <Popover>
                            <PopoverTrigger asChild>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-8 px-2 rounded-full bg-white hover:bg-gray-50 flex items-center"
                              >
                                <Plus size={16} />
                              </Button>
                            </PopoverTrigger>
                            <PopoverContent
                              className="w-48 p-3"
                              align="start"
                              side="top"
                              sideOffset={8}
                            >
                              <button
                                type="button"
                                onClick={() => handleFileUpload("file")}
                                className="w-full flex items-center gap-3 px-2 py-2 rounded hover:bg-gray-100 transition-colors text-left"
                              >
                                <FileText className="h-4 w-4 text-gray-600" />
                                <span className="text-sm text-gray-900">
                                  Upload Files
                                </span>
                              </button>
                              <button
                                type="button"
                                onClick={() => handleFileUpload("image")}
                                className="w-full flex items-center gap-3 px-2 py-2 rounded hover:bg-gray-100 transition-colors text-left"
                              >
                                <ImageIcon className="h-4 w-4 text-gray-600" />
                                <span className="text-sm text-gray-900">
                                  Upload Images
                                </span>
                              </button>
                            </PopoverContent>
                          </Popover>

                          {/* Tools toggle - only show if assistant has linked tools */}
                          {linkedTools && linkedTools.length > 0 && (
                            <PromptInputButton
                              type="button"
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                console.log(
                                  "Tools button clicked, opening modal",
                                );
                                setIsToolModalOpen(true);
                              }}
                              title="Manage AI tools"
                            >
                              <Wand2 size={16} />{" "}
                              <span className="hidden sm:inline">Tools</span>
                            </PromptInputButton>
                          )}

                          {/* Model selection hidden for now */}
                        </PromptInputTools>

                        {/* Right-aligned action buttons */}
                        <div className="flex items-center gap-1">
                          {/* Voice Dictation Button */}
                          <PromptInputButton
                            type="button"
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              toast({
                                title: "Voice mode coming soon",
                                description:
                                  "We're working on adding voice dictation functionality.",
                              });
                            }}
                          >
                            <Mic size={16} />
                          </PromptInputButton>

                          <PromptInputSubmit
                            disabled={
                              !localInput?.trim() &&
                              attachedFiles.length === 0 &&
                              status !== "streaming" &&
                              status !== "submitted"
                            }
                            status={
                              status === "submitted" || status === "streaming"
                                ? "streaming"
                                : "ready"
                            }
                            style={{
                              backgroundColor: brandColor,
                            }}
                            onClick={(e) => {
                              if (
                                status === "streaming" ||
                                status === "submitted"
                              ) {
                                e.preventDefault();
                                stop();
                              }
                            }}
                          />
                        </div>
                      </PromptInputToolbar>
                    </PromptInput>

                    {/* Hidden FileUpload components */}
                    <div style={{ display: "none" }}>
                      <div ref={fileUploadRef}>
                        <FileUpload
                          onFilesChange={(files) => {
                            setAttachedFiles((prev) => [...prev, ...files]);
                          }}
                          maxFiles={10}
                          value={[]}
                          mode="file"
                        />
                      </div>
                      <div ref={imageUploadRef}>
                        <FileUpload
                          onFilesChange={(files) => {
                            setAttachedFiles((prev) => [...prev, ...files]);
                          }}
                          maxFiles={10}
                          value={[]}
                          mode="image"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Settings Modal */}
      <Dialog open={isSettingsModalOpen} onOpenChange={setIsSettingsModalOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Assistant Settings</DialogTitle>
          </DialogHeader>

          <div className="space-y-6">
            {/* Title */}
            <div className="space-y-2">
              <Label htmlFor="title">Title</Label>
              <Input
                id="title"
                value={settingsForm.title}
                onChange={(e) => {
                  setSettingsForm((prev) => ({
                    ...prev,
                    title: e.target.value,
                  }));
                }}
                placeholder={baseAssistant?.title || "Enter assistant title..."}
              />
            </div>

            {/* Name */}
            <div className="space-y-2">
              <Label htmlFor="name">Name</Label>
              <Input
                id="name"
                value={settingsForm.name}
                onChange={(e) => {
                  setSettingsForm((prev) => ({
                    ...prev,
                    name: e.target.value,
                  }));
                }}
                placeholder={baseAssistant?.name || "Enter assistant name..."}
              />
            </div>

            {/* Description */}
            <div className="space-y-2">
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                value={settingsForm.description}
                onChange={(e) => {
                  setSettingsForm((prev) => ({
                    ...prev,
                    description: e.target.value,
                  }));
                }}
                placeholder={
                  baseAssistant?.description || "Enter assistant description..."
                }
                rows={3}
              />
            </div>

            {/* Chat Welcome Message */}
            <div className="space-y-2">
              <Label htmlFor="chat-welcome-message">Chat Welcome Message</Label>
              <Textarea
                id="chat-welcome-message"
                value={settingsForm.chat_welcome_message}
                onChange={(e) => {
                  setSettingsForm((prev) => ({
                    ...prev,
                    chat_welcome_message: e.target.value,
                  }));
                }}
                placeholder="Enter a custom welcome message for chat conversations..."
                rows={2}
              />
            </div>

            {/* Custom Instructions */}
            <div className="space-y-2">
              <Label htmlFor="custom-instructions">Custom Instructions</Label>
              <Textarea
                id="custom-instructions"
                value={settingsForm.custom_instructions}
                onChange={(e) => {
                  setSettingsForm((prev) => ({
                    ...prev,
                    custom_instructions: e.target.value,
                  }));
                }}
                placeholder="Additional instructions for this assistant..."
                rows={4}
              />
            </div>

            {/* CTA Label */}
            <div className="space-y-2">
              <Label htmlFor="cta-label">Button Label</Label>
              <Input
                id="cta-label"
                value={settingsForm.cta_label}
                onChange={(e) => {
                  setSettingsForm((prev) => ({
                    ...prev,
                    cta_label: e.target.value,
                  }));
                }}
                placeholder={baseAssistant?.cta_label || "Send"}
              />
            </div>

            {/* Action Buttons */}
            <div className="flex justify-end gap-3 pt-4 border-t">
              <Button
                variant="outline"
                onClick={() => setIsSettingsModalOpen(false)}
              >
                Cancel
              </Button>
              <Button
                onClick={() => updateInstanceMutation.mutate(settingsForm)}
                disabled={updateInstanceMutation.isPending}
              >
                {updateInstanceMutation.isPending
                  ? "Saving..."
                  : "Save Settings"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Assistant Edit Sheet (non-chat assistants) */}
      {(() => {
        const isOrgOwnedAssistant = true;

        const isChatAssistant =
          (baseAssistant as any)?.supports_chat === true ||
          (baseAssistant as any)?.generation_mode === "chat";

        return (
          <Sheet
            open={showAssistantEditModal}
            onOpenChange={setShowAssistantEditModal}
          >
            <SheetContent className="w-[60%] max-w-none p-0 overflow-hidden">
              <div className="flex flex-col h-full">
                <SheetHeader className="p-6 pb-0">
                  <SheetTitle>Edit AI Assistant</SheetTitle>
                  <div id="assistant-tabs-portal" className="mt-2" />
                </SheetHeader>
                <div className="flex-1 overflow-y-auto p-6">
                  {baseAssistant && (
                    <AIAssistantForm
                      initialData={baseAssistant}
                      isChatAssistant={isChatAssistant}
                      onSubmit={(data) =>
                        updateSystemAssistantMutation.mutate(data)
                      }
                      onCancel={() => setShowAssistantEditModal(false)}
                      isLoading={updateSystemAssistantMutation.isPending}
                    />
                  )}
                </div>
              </div>
            </SheetContent>
          </Sheet>
        );
      })()}

      {/* Archive Confirmation Dialog */}
      <Dialog
        open={!!threadToArchive}
        onOpenChange={() => setThreadToArchive(null)}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Archive Conversation</DialogTitle>
            <DialogDescription>
              Are you sure you want to archive this conversation? Archived
              conversations will no longer be visible in your chat list, but the
              data will be preserved.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setThreadToArchive(null)}
              disabled={isArchiving}
            >
              Cancel
            </Button>
            <Button
              onClick={() => threadToArchive && archiveThread(threadToArchive)}
              disabled={isArchiving}
            >
              {isArchiving ? "Archiving..." : "Archive"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Tools Management Modal */}
      <Dialog open={isToolModalOpen} onOpenChange={setIsToolModalOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] flex flex-col">
          <DialogHeader className="flex-shrink-0">
            <DialogTitle className="flex items-center gap-2">
              <Wrench size={20} />
              AI Tools
            </DialogTitle>
            <DialogDescription>
              This AI assistant has access to the following AI tools. It will
              automatically use the appropriate tools based on your request.
            </DialogDescription>
          </DialogHeader>

          <div className="flex-1 overflow-y-auto pr-2">
            <div className="space-y-3">
              {toolInstances.map((g) => {
                return (
                  <div
                    key={g.id}
                    className="flex items-center gap-3 p-2 bg-gray-50 rounded-lg border"
                  >
                    {/* Assistant Icon */}
                    <div className="flex-shrink-0 w-8 h-8 bg-white rounded-lg border flex items-center justify-center text-sm overflow-hidden">
                      {typeof g.icon === "string" &&
                      g.icon.startsWith("http") ? (
                        <img
                          src={g.icon}
                          alt={g.title}
                          className="w-full h-full object-contain"
                        />
                      ) : (
                        <span>{g.icon}</span>
                      )}
                    </div>

                    {/* Assistant Info */}
                    <div className="flex-1 min-w-0 flex items-center">
                      <h4 className="font-medium text-gray-900 truncate">
                        {g.title}
                      </h4>
                    </div>
                  </div>
                );
              })}

              {toolInstances.length === 0 && (
                <div className="text-center py-8 text-gray-500">
                  <Wrench size={48} className="mx-auto mb-3 text-gray-300" />
                  <p>No AI tools found for this assistant.</p>
                </div>
              )}
            </div>
          </div>

          {/* No footer buttons needed in read-only mode */}
        </DialogContent>
      </Dialog>
    </MainLayout>
  );
}
